import { DEFAULT_PAGE_SIZE } from '@w7/ui'
/**
 * The console's data client.
 *
 * THIS FILE IS THE SWAP POINT. Every screen talks to these functions and to
 * nothing else; each one is already async, already paged, already returns the
 * staleness envelope where the real projection has one. Pointing the console at
 * live services means reimplementing this module against fetch — no screen,
 * chart or table changes.
 *
 * Until then it reads the deterministic fixture world in `world.ts`, with
 * artificial latency so loading and skeleton states are exercised in
 * development rather than discovered in production.
 */

import { gameBucket, getWorld, type EventInstance, type World } from './world'
import { simulatedExchangeBook, simulatedLadder } from './prices'
import {
  compareHits,
  matchField,
  SEARCH_SCORE,
  scoreMatch,
  type FieldMatch,
  type FieldWeight,
} from './search'
import {
  ROLE_LABEL,
  ROLE_SHORT,
  creatableRoles,
  type Account,
  type AccountRecord,
  type ActivityEvent,
  type Bet,
  type BetSide,
  type BetStatus,
  type BookBranchRow,
  type BookPosition,
  type DailyPoint,
  type DateRange,
  type EventExposure,
  type MarketExposure,
  type MarketType,
  type Paged,
  type PnlRow,
  type Projected,
  type Role,
  type RunnerPrices,
  type SearchGroup,
  type SearchHit,
  type SearchKind,
  type SearchResult,
  type Session,
  type SimulatedExchangeBook,
  type Sport,
  type StopState,
  type StoppedBy,
  type Transfer,
} from './types'
import {
  governingSuspension,
  isStrictAncestor,
  STOP_COPY,
  stopStateOf,
  stoppedByOf,
  type Suspension,
} from '@/lib/status'
import {
  availableToGive,
  bookBySelection,
  legOf,
  limitUsage,
  round2,
  settlementPlan,
  type SettlementPlan,
} from '@/lib/book'
import { ago, count, EMPTY, isoDay, lll, money, toneOf } from '@/lib/format'

/** Re-exported so the palette's client-side action matcher ranks on ONE ladder. */
export { SEARCH_SCORE, matchField, compareHits, SEARCH_TOKEN_SPLIT } from './search'
export type { MatchQuality, FieldMatch, FieldWeight } from './search'

/**
 * Re-exported from `types.ts` so every consumer imports its whole search
 * contract from the swap point, the way the rest of this module's shapes are
 * reached. When these become a live `GET /v1/search`, one import changes.
 */
export type { SearchGroup, SearchHit, SearchKind, SearchResult } from './types'

/** Set to 0 in tests. Small enough not to annoy, large enough to see skeletons. */
export const LATENCY = { fast: 120, normal: 260, slow: 420 }

const delay = <T>(value: T, ms: number): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms))

/**
 * How often live surfaces re-poll. Surfaced in the UI verbatim because v1 has
 * no realtime push and the spec requires polling surfaces to say that they poll
 * (specs/features/agent-console.yaml non_goals[5]).
 */
export const POLL_INTERVAL_MS = 30_000

/* ------------------------------------------------------------------ scope -- */

const inRange = (iso: string, range?: DateRange) => {
  if (!range) return true
  const day = iso.slice(0, 10)
  return day >= range.from && day <= range.to
}

/** Every read is scoped to the caller's subtree. Anything outside is ABSENT. */
function scopeIds(rootId: string): Set<string> {
  return new Set(getWorld().subtree.get(rootId) ?? [rootId])
}

/**
 * Resolve a requested hierarchy root against the caller's OWN subtree.
 *
 * Every report can now be re-read as if signed in as an agent beneath you, which
 * means `rootId` has become an attacker-controlled parameter: it arrives from a
 * URL. Without this guard, pasting a sibling branch's id into `?scope=` would
 * return that branch's book — the console's single most important invariant,
 * silently broken by a query string.
 *
 * Returns null for a root outside the subtree. Callers then return an EMPTY
 * result, never an error: absence and no-access must stay indistinguishable, the
 * same rule `getAccount` already follows.
 */
function resolveRoot(rootId?: string): { rootId: string; scope: Set<string> } | null {
  const world = getWorld()
  const id = rootId ?? world.session.accountId
  const own = scopeIds(world.session.accountId)
  if (!own.has(id)) return null
  return { rootId: id, scope: scopeIds(id) }
}

/**
 * Freshness of the exposure projection, in one place.
 *
 * `getExposure`, `getMarketBook` and `searchConsole` all render figures out of
 * the same projection, so they must all agree about whether it has fallen
 * behind. Two independently-computed staleness flags is how one surface shows a
 * banner while another next to it shows a number.
 */
function projectionState(): { asOf: string; stale: boolean } {
  const world = getWorld()
  return { asOf: new Date(world.now - 4_000).toISOString(), stale: false }
}

export function defaultRange(days = 30): DateRange {
  const world = getWorld()
  const to = isoDay(world.now)
  const from = isoDay(world.now - (days - 1) * 86_400_000)
  return { from, to }
}

/* ------------------------------------------------------- account status --- */

/**
 * Suspensions, keyed by the account they were placed ON, one map per world.
 *
 * Keyed on the world object rather than held as a module singleton so
 * `resetWorld()` genuinely resets them — a suspension surviving into the next
 * test would make the suite order-dependent in the one place that must not be.
 */
const suspensionStore = new WeakMap<World, Map<string, Suspension>>()

/**
 * The fixture ships accounts whose stored status is already `suspended` but
 * which carry no record of who stopped them or why. They are seeded on first
 * read so the store is the single source of truth from the very first render:
 * without this a stopped account would offer a Lift button that then refused
 * with "not stopped", which is the worst kind of wrong.
 *
 * The reason says exactly what it is. Inventing a plausible one would put words
 * in an operator's mouth on an audit trail.
 */
function suspensions(world: World): Map<string, Suspension> {
  const existing = suspensionStore.get(world)
  if (existing) return existing
  const seeded = new Map<string, Suspension>()
  for (const account of world.accounts) {
    if (account.status !== 'suspended') continue
    const parent = account.parentId ? world.byId.get(account.parentId) : null
    seeded.set(account.id, {
      accountId: account.id,
      byId: parent?.id ?? account.id,
      byUsername: parent?.username ?? account.username,
      at: account.lastActiveAt ?? account.createdAt,
      reason: 'Reason not recorded',
      // Nothing records what these were before they were stopped, so a lift puts
      // them back as active — the only status the history supports.
      previousStatus: 'active',
    })
  }
  suspensionStore.set(world, seeded)
  return seeded
}

/**
 * The one function that turns a stored row into an `Account`.
 *
 * Every read path goes through it, and the compiler is what makes that true:
 * the world stores `AccountRecord`, so a path that forgot to work out whether an
 * account is stopped does not typecheck.
 */
function statusViewer(world: World = getWorld()): {
  view: (record: AccountRecord) => Account
  viewOrNull: (record: AccountRecord | null | undefined) => Account | null
} {
  const records = suspensions(world)
  const callerId = world.session.accountId
  const callerSubtree = scopeIds(callerId)
  const usernameOf = (id: string) => world.byId.get(id)?.username ?? id

  const view = (record: AccountRecord): Account => ({
    ...record,
    ...stopStateOf({ account: record, suspensions: records, usernameOf, callerId, callerSubtree }),
  })
  return { view, viewOrNull: (record) => (record ? view(record) : null) }
}

/** What a stop reaches. Measured before the act, so the confirm can be specific. */
export interface StopImpact {
  /** Agents strictly beneath this account. */
  agents: number
  /** Members strictly beneath it. Stopping an agent with 39 members is not the
   *  same act as stopping one member, and the confirm has to say so. */
  members: number
  /**
   * Worst-case open liability for this account and everything beneath it.
   *
   * Read straight off the exposure projection, which is already a subtree
   * roll-up. Summing descendants' exposures would count the same bet once per
   * rung of the ladder.
   */
  openExposure: number
  /** Open bets that keep running — a stop does not cancel a bet or settle one. */
  openBets: number
  /** Accounts beneath that are ALREADY stopped; this act does not change them. */
  alreadyStopped: number
}

function computeStopImpact(world: World, subject: AccountRecord): StopImpact {
  const records = suspensions(world)
  const ids = world.subtree.get(subject.id) ?? [subject.id]
  let agents = 0
  let members = 0
  let alreadyStopped = 0
  for (const id of ids) {
    if (id === subject.id) continue
    const account = world.byId.get(id)
    if (!account) continue
    if (account.role === 'player') members += 1
    else agents += 1
    if (governingSuspension(account, records)) alreadyStopped += 1
  }
  const beneath = new Set(ids)
  const openBets = world.bets.filter(
    (b) => beneath.has(b.memberId) && (b.status === 'matched' || b.status === 'unmatched'),
  ).length
  return { agents, members, openExposure: subject.exposure, openBets, alreadyStopped }
}

/**
 * The blast radius of stopping one account, for the confirm step.
 *
 * Null when the account is not inside the caller's subtree — absence and
 * no-access stay indistinguishable, exactly as `getAccount` treats them.
 */
export async function stopImpact(accountId: string): Promise<StopImpact | null> {
  const world = getWorld()
  if (!scopeIds(world.session.accountId).has(accountId)) return delay(null, LATENCY.fast)
  const subject = world.byId.get(accountId)
  if (!subject) return delay(null, LATENCY.fast)
  return delay(computeStopImpact(world, subject), LATENCY.fast)
}

export type StatusAction = 'suspend' | 'reactivate'

export type StatusRefusalCode =
  /** Not visible to the caller, or above them. Never confirms which. */
  | 'outside-downline'
  | 'self'
  | 'reason-required'
  | 'already-stopped'
  | 'stopped-higher-up'
  | 'not-stopped'
  /** The lift rule: placed by an upline, so only they can lift it. */
  | 'stopped-above'
  | 'key-reused'

/**
 * A refused status change, carrying WHY in a form the UI can branch on.
 *
 * `message` is already the sentence to show — the same sentence `stopControl`
 * put on the disabled button, from `STOP_COPY`.
 */
export class StatusRefusal extends Error {
  readonly code: StatusRefusalCode
  constructor(code: StatusRefusalCode, message: string) {
    super(message)
    this.name = 'StatusRefusal'
    this.code = code
  }
}

export interface SetAccountStatusInput {
  accountId: string
  action: StatusAction
  /** REQUIRED to suspend, optional to reactivate. Trimmed; blank counts as absent. */
  reason?: string
  /** One key per opened dialog — replaying it must not write a second audit row. */
  idempotencyKey: string
}

export interface SetAccountStatusResult {
  action: StatusAction
  /** The subject re-read AFTER the write. Never an optimistic local value. */
  account: Account
  /** The suspension now standing ON THE SUBJECT, or null once it is lifted. */
  suspension: StoppedBy | null
  /** What the act reached, measured before it ran. */
  impact: StopImpact
  /** The audit row written to /activity. */
  event: ActivityEvent
  /** True when the key had already been applied and nothing was written again. */
  replayed: boolean
}

/** Applied keys per world, for the same reason the suspensions are. */
const statusKeyStore = new WeakMap<World, Map<string, SetAccountStatusResult>>()

function statusKeys(world: World): Map<string, SetAccountStatusResult> {
  const existing = statusKeyStore.get(world)
  if (existing) return existing
  const fresh = new Map<string, SetAccountStatusResult>()
  statusKeyStore.set(world, fresh)
  return fresh
}

let statusEventSeq = 0

/**
 * Stop or restart an account anywhere in the caller's subtree.
 *
 * The authority model is deliberately not the credit one. Credit moves only
 * between an account and its DIRECT parent; a stop may be placed by any strict
 * ancestor at any depth, so a Company Master who sees something suspicious five
 * levels down stops it immediately rather than through four intermediaries.
 *
 * Nothing is written onto descendants. The suspension lands on the account
 * actually stopped and everyone beneath derives from it, which is what lets one
 * lift restore the whole branch with every descendant's own status intact.
 *
 * A stop does NOT cancel open bets and does NOT touch settlement — it stops new
 * activity, and the UI says exactly that rather than overstating the button.
 */
export async function setAccountStatus(
  input: SetAccountStatusInput,
): Promise<SetAccountStatusResult> {
  const world = getWorld()
  const applied = statusKeys(world)

  const previous = applied.get(input.idempotencyKey)
  if (previous) {
    if (previous.account.id !== input.accountId || previous.action !== input.action) {
      throw new StatusRefusal(
        'key-reused',
        'That confirmation has already been used for a different change.',
      )
    }
    // Replay: no second write and no second audit row, but the account is re-read
    // so a retry reports the state things are actually in now.
    const record = world.byId.get(previous.account.id)
    const { view } = statusViewer(world)
    return delay(
      { ...previous, ...(record ? { account: view(record) } : {}), replayed: true },
      LATENCY.fast,
    )
  }

  const caller = world.byId.get(world.session.accountId)
  if (!caller) throw new StatusRefusal('outside-downline', STOP_COPY.outside)

  const subject = world.byId.get(input.accountId)
  if (subject && subject.id === caller.id) throw new StatusRefusal('self', STOP_COPY.self)
  // An upline and an account that does not exist produce the SAME refusal. A
  // distinct "you are not allowed" would confirm the account is real.
  if (!subject || !isStrictAncestor(caller.id, subject)) {
    throw new StatusRefusal('outside-downline', STOP_COPY.outside)
  }

  const records = suspensions(world)
  const usernameOf = (id: string) => world.byId.get(id)?.username ?? id
  const governing = governingSuspension(subject, records)
  const at = new Date().toISOString()

  let event: ActivityEvent
  /**
   * Measured on whichever side of the act answers the operator's question: what
   * ELSE stops (before a suspend), and what stays stopped anyway (after a lift).
   * Measured before a lift, `alreadyStopped` would count the entire branch,
   * since the record being removed is what is stopping all of it.
   */
  let impact: StopImpact

  if (input.action === 'suspend') {
    const reason = (input.reason ?? '').trim()
    if (!reason) throw new StatusRefusal('reason-required', STOP_COPY.reasonRequired)
    if (governing) {
      throw governing.accountId === subject.id
        ? new StatusRefusal(
            'already-stopped',
            STOP_COPY.alreadyStopped(governing.byUsername, governing.at),
          )
        : new StatusRefusal(
            'stopped-higher-up',
            STOP_COPY.stoppedHigherUp(usernameOf(governing.accountId)),
          )
    }

    impact = computeStopImpact(world, subject)
    records.set(subject.id, {
      accountId: subject.id,
      byId: caller.id,
      byUsername: caller.username,
      at,
      reason,
      // Captured so the lift restores what was there, not a cheerful 'active'.
      previousStatus: subject.status,
    })
    // The ONE row that is written. Descendants are untouched by design.
    subject.status = 'suspended'

    event = {
      id: `act-status-${++statusEventSeq}`,
      at,
      kind: 'status-changed',
      actorId: caller.id,
      subjectId: subject.id,
      summary: `Stopped ${subject.username}`,
      detail: reason,
    }
  } else {
    const own = records.get(subject.id)
    if (!own) {
      if (governing) {
        throw new StatusRefusal(
          'stopped-higher-up',
          STOP_COPY.stoppedHigherUp(usernameOf(governing.accountId)),
        )
      }
      throw new StatusRefusal('not-stopped', STOP_COPY.notStopped(subject.username))
    }
    // The lift rule. Anyone at or above whoever placed it may lift it; an upline's
    // stop is not the caller's to remove, and saying who placed it is the point —
    // the operator's next move is to go and ask them.
    if (own.byId !== caller.id && !scopeIds(caller.id).has(own.byId)) {
      throw new StatusRefusal('stopped-above', STOP_COPY.stoppedAbove(own.byUsername))
    }

    records.delete(subject.id)
    subject.status = own.previousStatus
    impact = computeStopImpact(world, subject)

    const reason = (input.reason ?? '').trim()
    event = {
      id: `act-status-${++statusEventSeq}`,
      at,
      kind: 'status-changed',
      actorId: caller.id,
      subjectId: subject.id,
      summary: `Restarted ${subject.username}`,
      // With no reason given, the trail still records what is being undone.
      detail: reason || `Stopped by ${own.byUsername} on ${lll(own.at)} — ${own.reason}`,
    }
  }

  // `world.activity` is newest-first, so a new row goes on the front.
  world.activity.unshift(event)

  const { view } = statusViewer(world)
  const standing = records.get(subject.id)
  const result: SetAccountStatusResult = {
    action: input.action,
    account: view(subject),
    suspension: standing ? stoppedByOf(standing, usernameOf) : null,
    impact,
    event,
    replayed: false,
  }
  applied.set(input.idempotencyKey, result)
  return delay(result, LATENCY.slow)
}

/* ---------------------------------------------------------------- session -- */

export async function getSession(): Promise<Session> {
  return delay(getWorld().session, LATENCY.fast)
}

export async function getAccount(id: string): Promise<Account | null> {
  const world = getWorld()
  const scope = scopeIds(world.session.accountId)
  // Out of subtree = not found. Never "forbidden" — that would confirm it exists.
  if (!scope.has(id)) return delay(null, LATENCY.fast)
  return delay(statusViewer(world).viewOrNull(world.byId.get(id)), LATENCY.fast)
}

/** Upline chain from the signed-in account down to `id`, for a breadcrumb. */
export async function getUpline(id: string): Promise<Account[]> {
  const world = getWorld()
  // The hierarchy scope picker feeds this ids straight out of a URL, so it is a
  // scoped read like every other. Without the guard, an out-of-subtree id fell
  // through to `account.path` and handed back a sibling branch's whole ancestry.
  if (!scopeIds(world.session.accountId).has(id)) return delay([], LATENCY.fast)
  const account = world.byId.get(id)
  if (!account) return delay([], LATENCY.fast)
  const sessionIndex = account.path.indexOf(world.session.accountId)
  const visible = sessionIndex >= 0 ? account.path.slice(sessionIndex) : account.path
  const { view } = statusViewer(world)
  return delay(
    visible
      .map((pid) => world.byId.get(pid))
      .filter((a): a is AccountRecord => Boolean(a))
      .map(view),
    LATENCY.fast,
  )
}

/* --------------------------------------------------------------- downline -- */

export type DownlineSort =
  | 'username'
  | 'balance'
  | 'exposure'
  | 'creditLimit'
  | 'settlement'
  | 'lastActiveAt'

export interface DownlineQuery {
  /** Defaults to the signed-in account. */
  rootId?: string
  search?: string
  roles?: Role[]
  /**
   * The account's OWN stored status. An account stopped because an AGENT ABOVE
   * it was stopped keeps its own status, so this will not find it — use
   * `stopped` for the question operators actually ask.
   */
  statuses?: Account['status'][]
  /**
   * Effective status, chain included: true = only accounts currently stopped,
   * false = only accounts that are not. This is what answers "show me everything
   * that is stopped right now", including whole branches stopped from above.
   */
  stopped?: boolean
  /** Only accounts with open exposure. */
  hasExposure?: boolean
  /** Only accounts carrying an attention flag. */
  needsAttention?: boolean
  /**
   * The Monday-settling filter.
   * 'collect' = they owe you (settlement > 0); 'pay' = you owe them (< 0).
   *
   * `needsAttention` is not a substitute: it only catches |settlement| > 1000 and
   * drags in dormancy and limit flags too. Settling needs the EXACT set,
   * including the ₹40 rows, so the selection bar can honestly say "select all 23".
   */
  settlement?: 'collect' | 'pay'
  /** Direct children only, rather than the whole subtree. */
  directOnly?: boolean
  page?: number
  pageSize?: number
  sort?: DownlineSort
  direction?: 'asc' | 'desc'
}

export interface DownlineResult extends Paged<Account> {
  totals: {
    creditLimit: number
    balance: number
    exposure: number
    /** Positive settlement across the page's scope — what is owed to the caller. */
    toCollect: number
    /** Negative settlement — what the caller owes down the chain. */
    toPay: number
    members: number
    agents: number
  }
}

const EMPTY_DOWNLINE: DownlineResult = {
  rows: [],
  total: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totals: { creditLimit: 0, balance: 0, exposure: 0, toCollect: 0, toPay: 0, members: 0, agents: 0 },
}

export async function listDownline(query: DownlineQuery = {}): Promise<DownlineResult> {
  const world = getWorld()
  const resolved = resolveRoot(query.rootId)
  if (!resolved) return delay({ ...EMPTY_DOWNLINE, pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE }, LATENCY.fast)
  const { rootId } = resolved
  const {
    search = '',
    roles,
    statuses,
    stopped,
    hasExposure,
    needsAttention,
    settlement,
    directOnly = false,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = 'username',
    direction = 'asc',
  } = query

  const ids = directOnly
    ? (world.children.get(rootId) ?? [])
    : (world.subtree.get(rootId) ?? []).filter((id) => id !== rootId)

  const { view } = statusViewer(world)
  const needle = search.trim().toLowerCase()
  let rows = ids
    .map((id) => world.byId.get(id))
    .filter((a): a is AccountRecord => Boolean(a))
    // Viewed BEFORE the filters, so `stopped` narrows on the same derived status
    // the rows will render — filtering on the stored one would hide exactly the
    // branch an operator is looking for.
    .map(view)
    .filter((a) => !needle || a.username.toLowerCase().includes(needle) || a.displayName.toLowerCase().includes(needle))
    .filter((a) => !roles?.length || roles.includes(a.role))
    .filter((a) => !statuses?.length || statuses.includes(a.status))
    .filter((a) => stopped === undefined || (a.effectiveStatus === 'suspended') === stopped)
    .filter((a) => !hasExposure || a.exposure > 0)
    .filter((a) => !needsAttention || a.flags.length > 0)
    .filter((a) =>
      !settlement ? true : settlement === 'collect' ? a.settlement > 0 : a.settlement < 0,
    )

  const factor = direction === 'asc' ? 1 : -1
  rows = [...rows].sort((a, b) => {
    switch (sort) {
      case 'username':
        return a.username.localeCompare(b.username, 'en-IN') * factor
      case 'lastActiveAt': {
        const av = a.lastActiveAt ? Date.parse(a.lastActiveAt) : -1
        const bv = b.lastActiveAt ? Date.parse(b.lastActiveAt) : -1
        return (av - bv) * factor
      }
      default:
        return ((a[sort] as number) - (b[sort] as number)) * factor
    }
  })

  const totals = rows.reduce(
    (acc, row) => {
      acc.creditLimit += row.creditLimit
      acc.balance += row.balance
      acc.exposure += row.exposure
      if (row.settlement > 0) acc.toCollect += row.settlement
      else acc.toPay += -row.settlement
      if (row.role === 'player') acc.members += 1
      else acc.agents += 1
      return acc
    },
    { creditLimit: 0, balance: 0, exposure: 0, toCollect: 0, toPay: 0, members: 0, agents: 0 },
  )

  const start = (page - 1) * pageSize
  return delay(
    {
      rows: rows.slice(start, start + pageSize),
      total: rows.length,
      page,
      pageSize,
      totals: {
        creditLimit: round2(totals.creditLimit),
        balance: round2(totals.balance),
        exposure: round2(totals.exposure),
        toCollect: round2(totals.toCollect),
        toPay: round2(totals.toPay),
        members: totals.members,
        agents: totals.agents,
      },
    },
    LATENCY.normal,
  )
}

/** Children of one account, for lazy tree expansion. */
export async function listChildren(parentId: string): Promise<Account[]> {
  const world = getWorld()
  const scope = scopeIds(world.session.accountId)
  if (!scope.has(parentId)) return delay([], LATENCY.fast)
  const { view } = statusViewer(world)
  return delay(
    (world.children.get(parentId) ?? [])
      .map((id) => world.byId.get(id))
      .filter((a): a is AccountRecord => Boolean(a))
      .map(view)
      .sort((a, b) => a.username.localeCompare(b.username, 'en-IN')),
    LATENCY.fast,
  )
}

/* --------------------------------------------------------------- overview -- */

export interface OverviewSnapshot {
  account: Account
  /** Day-grain series across the requested range, oldest first. */
  daily: DailyPoint[]
  /** Same length window immediately before, for period-over-period deltas. */
  previous: DailyPoint[]
  bySport: { sport: Sport; turnover: number; netPnl: number; bets: number }[]
  byGame: { game: string; turnover: number; netPnl: number }[]
  topAgents: { account: Account; turnover: number; netPnl: number; members: number }[]
  topMembers: { account: Account; turnover: number; netPnl: number; bets: number }[]
  attention: { account: Account; reason: string; severity: 'warn' | 'error' }[]
  recentTransfers: Transfer[]
  openBets: number
  unmatchedStake: number
  activeMembers: number
  totalMembers: number
}

export interface OverviewQuery {
  range: DateRange
  /** Read the whole snapshot as if signed in as this agent. Defaults to self. */
  rootId?: string
  sports?: Sport[]
  games?: string[]
}

/** Every day in the range, inclusive. Stepped by calendar date, not by 86.4M ms,
 *  so a DST-observing operator machine cannot lose or repeat a day. */
function dayKeys(range: DateRange): string[] {
  const out: string[] = []
  const cursor = new Date(`${range.from}T00:00:00`)
  if (Number.isNaN(cursor.getTime())) return out
  // 400 is a hard stop, not a policy: the range picker already rejects anything
  // over a year, and an unbounded loop on a hand-edited URL is not acceptable.
  for (let i = 0; i < 400 && isoDay(cursor) <= range.to; i++) {
    out.push(isoDay(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

/**
 * The day-grain series, computed from the SAME filtered rows the rest of the
 * snapshot is built from.
 *
 * `world.daily` is precomputed for the signed-in subtree and knows nothing about
 * sport, game bucket or a scoped root. Serving it beside filtered tiles would
 * put a chart on screen that describes a different book to the numbers above it
 * — on a reporting surface that is a lie, not an approximation.
 */
function dailySeries(opts: {
  rootId: string
  scope: Set<string>
  range: DateRange
  sports?: Sport[]
  games?: string[]
}): DailyPoint[] {
  const world = getWorld()
  const { rootId, scope, range, sports, games } = opts
  const keys = dayKeys(range)
  const points = new Map<string, DailyPoint>(
    keys.map((date) => [
      date,
      {
        date,
        turnover: 0,
        grossPnl: 0,
        commission: 0,
        netPnl: 0,
        bets: 0,
        activeMembers: 0,
        newMembers: 0,
        creditIn: 0,
        creditOut: 0,
      },
    ]),
  )
  const active = new Map<string, Set<string>>(keys.map((d) => [d, new Set<string>()]))

  for (const b of world.bets) {
    if (!scope.has(b.memberId)) continue
    if (sports?.length && !sports.includes(b.sport)) continue
    if (games?.length && !games.includes(gameBucket(b.marketType))) continue
    const point = points.get(isoDay(b.placedAt))
    if (!point) continue
    point.turnover = round2(point.turnover + b.matched)
    point.bets += 1
    active.get(isoDay(b.placedAt))?.add(b.memberId)
  }

  for (const r of world.pnl) {
    if (!scope.has(r.memberId)) continue
    if (sports?.length && !sports.includes(r.sport)) continue
    if (games?.length && !games.includes(r.game)) continue
    const point = points.get(isoDay(r.settledAt))
    if (!point) continue
    // The book's result is the mirror of the member's.
    point.grossPnl = round2(point.grossPnl - r.memberPnl)
    point.commission = round2(point.commission + r.commission)
    point.netPnl = round2(point.netPnl - r.netAmount)
  }

  // Credit movement has no sport and no game bucket, so it narrows by scope and
  // range only. Zeroing it under a sport filter would be worse: the chart would
  // claim no credit moved that week.
  for (const t of world.transfers) {
    const point = points.get(isoDay(t.at))
    if (!point) continue
    if (t.fromId === rootId) point.creditOut = round2(point.creditOut + t.amount)
    if (t.toId === rootId) point.creditIn = round2(point.creditIn + t.amount)
  }

  for (const a of world.accounts) {
    if (!scope.has(a.id)) continue
    const point = points.get(isoDay(a.createdAt))
    if (point) point.newMembers += 1
  }

  for (const [key, set] of active) {
    const point = points.get(key)
    if (point) point.activeMembers = set.size
  }

  return keys.map((k) => points.get(k)!)
}

/**
 * Returns null when `rootId` is not beneath the caller.
 *
 * Never a silent fall back to self: that would draw YOUR numbers under THEIR
 * name, which is the one failure mode a scoped report must not have.
 */
export async function getOverview(query: OverviewQuery): Promise<OverviewSnapshot | null> {
  const world = getWorld()
  const { range, sports, games } = query
  const resolved = resolveRoot(query.rootId)
  if (!resolved) return delay(null, LATENCY.fast)
  const { rootId, scope } = resolved
  const { view } = statusViewer(world)
  const account = view(world.byId.get(rootId)!)

  const daily = dailySeries({ rootId, scope, range, ...(sports ? { sports } : {}), ...(games ? { games } : {}) })
  const spanDays = Math.max(1, daily.length)
  const priorEnd = new Date(`${range.from}T00:00:00`).getTime() - 86_400_000
  const priorStart = priorEnd - (spanDays - 1) * 86_400_000
  const previous = dailySeries({
    rootId,
    scope,
    range: { from: isoDay(priorStart), to: isoDay(priorEnd) },
    ...(sports ? { sports } : {}),
    ...(games ? { games } : {}),
  })

  // Every derived block below reduces these two arrays, so filtering here is
  // what makes bySport / byGame / topAgents / topMembers / activeMembers /
  // unmatchedStake narrow honestly rather than each needing its own guard.
  const bets = world.bets
    .filter((b) => scope.has(b.memberId) && inRange(b.placedAt, range))
    .filter((b) => !sports?.length || sports.includes(b.sport))
    .filter((b) => !games?.length || games.includes(gameBucket(b.marketType)))
  const pnl = world.pnl
    .filter((p) => scope.has(p.memberId) && inRange(p.settledAt, range))
    .filter((p) => !sports?.length || sports.includes(p.sport))
    .filter((p) => !games?.length || games.includes(p.game))

  /* by sport */
  const sportMap = new Map<Sport, { turnover: number; netPnl: number; bets: number }>()
  for (const bet of bets) {
    const entry = sportMap.get(bet.sport) ?? { turnover: 0, netPnl: 0, bets: 0 }
    entry.turnover += bet.matched
    entry.bets += 1
    sportMap.set(bet.sport, entry)
  }
  for (const row of pnl) {
    const entry = sportMap.get(row.sport) ?? { turnover: 0, netPnl: 0, bets: 0 }
    entry.netPnl -= row.netAmount
    sportMap.set(row.sport, entry)
  }

  /* by game bucket */
  const gameMap = new Map<string, { turnover: number; netPnl: number }>()
  for (const row of pnl) {
    const entry = gameMap.get(row.game) ?? { turnover: 0, netPnl: 0 }
    entry.turnover += row.turnover
    entry.netPnl -= row.netAmount
    gameMap.set(row.game, entry)
  }

  /* per-account rollups */
  const perAccount = new Map<string, { turnover: number; netPnl: number; bets: number }>()
  const bump = (id: string, turnover: number, net: number, count: number) => {
    const entry = perAccount.get(id) ?? { turnover: 0, netPnl: 0, bets: 0 }
    entry.turnover += turnover
    entry.netPnl += net
    entry.bets += count
    perAccount.set(id, entry)
  }
  for (const bet of bets) {
    const member = world.byId.get(bet.memberId)
    if (!member) continue
    for (const id of [member.id, ...member.path]) bump(id, bet.matched, 0, id === member.id ? 1 : 0)
  }
  for (const row of pnl) {
    const member = world.byId.get(row.memberId)
    if (!member) continue
    for (const id of [member.id, ...member.path]) bump(id, 0, -row.netAmount, 0)
  }

  const directChildren = (world.children.get(account.id) ?? [])
    .map((id) => world.byId.get(id))
    .filter((a): a is AccountRecord => Boolean(a))
    .map(view)

  const topAgents = directChildren
    .filter((a) => a.role !== 'player')
    .map((a) => ({
      account: a,
      turnover: perAccount.get(a.id)?.turnover ?? 0,
      netPnl: perAccount.get(a.id)?.netPnl ?? 0,
      members: a.memberCount,
    }))
    .sort((a, b) => b.turnover - a.turnover)

  const topMembers = [...scope]
    .map((id) => world.byId.get(id))
    .filter((a): a is AccountRecord => a !== undefined && a.role === 'player')
    .map(view)
    .map((a) => ({
      account: a,
      turnover: perAccount.get(a.id)?.turnover ?? 0,
      netPnl: perAccount.get(a.id)?.netPnl ?? 0,
      bets: perAccount.get(a.id)?.bets ?? 0,
    }))
    .filter((m) => m.turnover > 0)
    .sort((a, b) => b.turnover - a.turnover)
    .slice(0, 10)

  /* attention list — operational only, never surveillance (non_goals[0]) */
  const attention: OverviewSnapshot['attention'] = []
  for (const id of scope) {
    const a = world.byId.get(id)
    if (!a || a.id === account.id) continue
    for (const flag of a.flags) {
      const reason =
        flag === 'exposure-over-limit'
          ? `Exposure is ${Math.round(limitUsage(a.exposure, a.creditLimit) * 100)}% of the credit limit — new bets will be rejected`
          : flag === 'exposure-near-limit'
            ? `Exposure has reached ${Math.round(limitUsage(a.exposure, a.creditLimit) * 100)}% of the credit limit`
            : flag === 'dormant-funded'
              ? 'No activity in 14 days but still holding a balance'
              : flag === 'settlement-overdue'
                ? 'Settlement outstanding for more than a week'
                : flag === 'never-logged-in'
                  ? 'Created but has never signed in'
                  : 'Sign-in is not provisioned yet'
      attention.push({
        account: view(a),
        reason,
        severity: flag === 'exposure-over-limit' ? 'error' : 'warn',
      })
    }
  }
  attention.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1))

  // No range filter on purpose: an open bet is open NOW, whatever window the
  // report is describing. Sport and game still apply, or the unmatched-stake
  // tile would contradict the sport the operator has selected.
  const openBets = world.bets
    .filter((b) => scope.has(b.memberId) && (b.status === 'matched' || b.status === 'unmatched'))
    .filter((b) => !sports?.length || sports.includes(b.sport))
    .filter((b) => !games?.length || games.includes(gameBucket(b.marketType)))

  const activeIds = new Set(bets.map((b) => b.memberId))

  return delay(
    {
      account,
      daily,
      previous,
      bySport: [...sportMap.entries()]
        .map(([sport, v]) => ({ sport, ...v, turnover: round2(v.turnover), netPnl: round2(v.netPnl) }))
        .sort((a, b) => b.turnover - a.turnover),
      byGame: [...gameMap.entries()]
        .map(([game, v]) => ({ game, turnover: round2(v.turnover), netPnl: round2(v.netPnl) }))
        .sort((a, b) => b.turnover - a.turnover),
      topAgents,
      topMembers,
      attention: attention.slice(0, 12),
      recentTransfers: world.transfers
        .filter((t) => t.fromId === account.id || t.toId === account.id)
        .slice(-8)
        .reverse(),
      openBets: openBets.length,
      unmatchedStake: round2(openBets.reduce((s, b) => s + b.unmatched, 0)),
      activeMembers: activeIds.size,
      totalMembers: account.memberCount,
    },
    LATENCY.slow,
  )
}

/* --------------------------------------------------------------- exposure -- */

export type ExposureSort = 'worst' | 'start' | 'event' | 'matched'

export interface ExposureQuery {
  rootId?: string
  sports?: Sport[]
  /** `my` = the caller's share of the book; `total` = the whole downline book. */
  basis?: 'my' | 'total'
  /** Matches the event name, any surviving market name, or any runner name. */
  search?: string
  /** Keep only these market types. Events left with no market drop out. */
  marketTypes?: MarketType[]
  /** Only events whose state is live. */
  inPlayOnly?: boolean
  /**
   * Magnitude floor on the event roll-up: keep if Math.abs(worstCase) >= this.
   * A magnitude because an operator says "worse than 1 lakh", never "worse than
   * minus one lakh".
   */
  worseThan?: number
  /** Focus one or more events by id — the palette's landing target, since this
   *  console has no event detail page to send an event result to. */
  eventIds?: string[]
  /** Server-side so the ordering is true across the whole book, not one page. */
  sort?: ExposureSort
}

const matchedOf = (e: EventExposure) => e.markets.reduce((s, m) => s + m.matched, 0)

/**
 * Default stays `worst`. The grid's whole argument is that its ordering IS the
 * risk ranking; `start` is the Monday-settling order, `matched` is where the
 * volume is, `event` is for finding a fixture you already know the name of.
 */
const EXPOSURE_SORTERS: Record<ExposureSort, (a: EventExposure, b: EventExposure) => number> = {
  worst: (a, b) => a.worstCase - b.worstCase,
  start: (a, b) => a.startsAt.localeCompare(b.startsAt),
  event: (a, b) => a.eventName.localeCompare(b.eventName, 'en-IN'),
  matched: (a, b) => matchedOf(b) - matchedOf(a),
}

/**
 * Roll one event's open bets up into an `EventExposure`.
 *
 * Shared by the grid and the market-detail page so the headline an operator
 * drills in FROM and the headline they land ON are the same arithmetic. Returns
 * null when no market survives, which is also how the market-type filter drops
 * an event.
 *
 * ORDER OF OPERATIONS IS LOAD-BEARING. `marketTypes` is applied while building
 * the market list, BEFORE the roll-up, so worstCase / bestCase / betCount /
 * memberCount describe only the markets that survived. Filtering after the
 * roll-up would leave an event shown as "Fancy only" still reporting a headline
 * that included its Match Odds book, and the KPI strip — which sums the returned
 * events — would disagree with the rows underneath it. A headline that does not
 * equal the sum of its visible rows is a bug, not a rounding artefact.
 */
function buildEventExposure(
  event: EventInstance,
  eventBets: Bet[],
  share: number,
  marketTypes?: MarketType[],
): EventExposure | null {
  const markets: MarketExposure[] = []
  for (const market of event.markets) {
    if (marketTypes?.length && !marketTypes.includes(market.type)) continue
    const marketBets = eventBets.filter((b) => b.marketId === market.id)
    if (marketBets.length === 0) continue
    const net = bookBySelection(marketBets.map(legOf), market.selections)
    const selections = market.selections.map((name) => ({
      name,
      net: round2((net.get(name) ?? 0) * share),
    }))
    const values = selections.map((s) => s.net)
    markets.push({
      marketId: market.id,
      marketName: market.name,
      marketType: market.type,
      selections,
      worstCase: round2(Math.min(...values)),
      bestCase: round2(Math.max(...values)),
      matched: round2(marketBets.reduce((s, b) => s + b.matched, 0)),
      unmatched: round2(marketBets.reduce((s, b) => s + b.unmatched, 0)),
      betCount: marketBets.length,
      memberCount: new Set(marketBets.map((b) => b.memberId)).size,
    })
  }
  if (markets.length === 0) return null

  const survivors = new Set(markets.map((m) => m.marketId))
  return {
    eventId: event.id,
    eventName: event.name,
    sport: event.sport,
    startsAt: event.startsAt,
    inPlay: event.state === 'live',
    markets: markets.sort((a, b) => a.worstCase - b.worstCase),
    // Conservative aggregation: markets settle independently, so the worst
    // cases add rather than offsetting.
    worstCase: round2(markets.reduce((s, m) => s + Math.min(0, m.worstCase), 0)),
    bestCase: round2(markets.reduce((s, m) => s + Math.max(0, m.bestCase), 0)),
    betCount: markets.reduce((s, m) => s + m.betCount, 0),
    memberCount: new Set(eventBets.filter((b) => survivors.has(b.marketId)).map((b) => b.memberId))
      .size,
  }
}

/** Open, matched money only — the set every exposure figure is computed from. */
function openBook(scope: Set<string>): Bet[] {
  return getWorld().bets.filter(
    (b) =>
      scope.has(b.memberId) &&
      (b.status === 'matched' || b.status === 'unmatched') &&
      b.matched > 0,
  )
}

function betsByEvent(bets: Bet[]): Map<string, Bet[]> {
  const byEvent = new Map<string, Bet[]>()
  for (const bet of bets) {
    const list = byEvent.get(bet.eventId) ?? []
    list.push(bet)
    byEvent.set(bet.eventId, list)
  }
  return byEvent
}

/**
 * Worst-case liability per event.
 *
 * Returned inside `Projected` so a screen physically cannot render the number
 * without also having the staleness flag in hand.
 */
export async function getExposure(query: ExposureQuery = {}): Promise<Projected<EventExposure[]>> {
  const world = getWorld()
  const state = projectionState()
  const resolved = resolveRoot(query.rootId)
  if (!resolved) return delay({ value: [], ...state }, LATENCY.fast)
  const { rootId, scope } = resolved
  const {
    sports,
    basis = 'total',
    search = '',
    marketTypes,
    inPlayOnly,
    worseThan,
    eventIds,
    sort = 'worst',
  } = query

  const share = basis === 'my' ? (world.byId.get(rootId)?.share.sportsbook ?? 100) / 100 : 1
  const needle = search.trim().toLowerCase()

  /**
   * Search runs AFTER the markets are built, not as a cheap pre-filter on the
   * event name. A hit on a runner name is only meaningful for a market that
   * actually carries an open position — searching the catalog would surface
   * events with no book at all. The trade is a full pass over the open book per
   * query, which is the right price at fixture scale.
   */
  const matchesSearch = (event: EventInstance, rolled: EventExposure) =>
    !needle ||
    event.name.toLowerCase().includes(needle) ||
    rolled.markets.some(
      (m) =>
        m.marketName.toLowerCase().includes(needle) ||
        m.selections.some((s) => s.name.toLowerCase().includes(needle)),
    )

  const events: EventExposure[] = []
  for (const [eventId, eventBets] of betsByEvent(openBook(scope))) {
    const event = world.eventById.get(eventId)
    if (!event) continue
    if (sports?.length && !sports.includes(event.sport)) continue

    const rolled = buildEventExposure(event, eventBets, share, marketTypes)
    if (!rolled) continue
    if (!matchesSearch(event, rolled)) continue
    events.push(rolled)
  }

  let out = events
  if (inPlayOnly) out = out.filter((e) => e.inPlay)
  if (eventIds?.length) out = out.filter((e) => eventIds.includes(e.eventId))
  // Applied last, against the post-filter roll-up, so "worse than ₹1L" means
  // worse than a lakh of the book the operator is actually looking at.
  if (worseThan && worseThan > 0) out = out.filter((e) => Math.abs(e.worstCase) >= worseThan)

  out = [...out].sort(EXPOSURE_SORTERS[sort] ?? EXPOSURE_SORTERS.worst)

  return delay({ value: out, ...state }, LATENCY.normal)
}

export interface MarketBookOptions {
  /** Applied exactly as `getExposure` applies it: share/100 for 'my', 1 for 'total'. */
  basis?: 'my' | 'total'
}

export interface MarketBook {
  event: EventExposure
  market: MarketExposure
  /** SIMULATED. Stable for a given marketId; never varies with time. */
  prices: RunnerPrices[]
  /** SIMULATED reconciliation rows. Never summed into anything real. */
  simulatedHedge: SimulatedExchangeBook
  /** Tree root — the signed-in account, whose book IS `market.selections`. */
  root: Pick<Account, 'id' | 'username' | 'displayName' | 'role' | 'childCount'>
  /**
   * Echoed back so the page can assert the figures it is drawing were computed
   * under the basis its toggle currently shows, rather than trusting that a
   * refetch has already landed. Flipping My share ↔ Total book and reading the
   * other basis' numbers is the worst possible bug on a money screen, because
   * both values are plausible.
   */
  basis: 'my' | 'total'
}

const marketBasisShare = (basis: 'my' | 'total'): number => {
  const world = getWorld()
  return basis === 'my'
    ? (world.byId.get(world.session.accountId)?.share.sportsbook ?? 100) / 100
    : 1
}

export async function getMarketBook(
  marketId: string,
  opts: MarketBookOptions = {},
): Promise<Projected<MarketBook | null>> {
  const world = getWorld()
  const state = projectionState()
  const scope = scopeIds(world.session.accountId)
  const basis = opts.basis ?? 'total'

  const event = world.events.find((e) => e.markets.some((m) => m.id === marketId))
  const market = event?.markets.find((m) => m.id === marketId)
  if (!event || !market) return delay({ value: null, ...state }, LATENCY.fast)

  const share = marketBasisShare(basis)
  const eventBets = openBook(scope).filter((b) => b.eventId === event.id)

  // Roll the WHOLE event up through the same helper the grid uses, so drilling
  // in cannot change the headline the operator drilled in from.
  const rolled = buildEventExposure(event, eventBets, share)
  const thisMarket =
    rolled?.markets.find((m) => m.marketId === marketId) ??
    // A market with no open book is a real, informative state — a flat book, not
    // an error. It renders at zero rather than blanking the page.
    ({
      marketId: market.id,
      marketName: market.name,
      marketType: market.type,
      selections: market.selections.map((name) => ({ name, net: 0 })),
      worstCase: 0,
      bestCase: 0,
      matched: 0,
      unmatched: 0,
      betCount: 0,
      memberCount: 0,
    } satisfies MarketExposure)

  const prices = simulatedLadder(market.id, market.selections, market.probs)
  const bestBackFor = (selection: string): number | null => {
    const runner = prices.find((p) => p.selection === selection)
    // `back` is outermost-first, so the BEST back price is the last element.
    return runner?.back[runner.back.length - 1]?.price ?? null
  }
  const simulatedHedge = simulatedExchangeBook(market.id, thisMarket.selections, bestBackFor)

  const self = world.byId.get(world.session.accountId)!

  return delay(
    {
      value: {
        event: rolled ?? {
          eventId: event.id,
          eventName: event.name,
          sport: event.sport,
          startsAt: event.startsAt,
          inPlay: event.state === 'live',
          markets: [],
          worstCase: 0,
          bestCase: 0,
          betCount: 0,
          memberCount: 0,
        },
        market: thisMarket,
        prices,
        simulatedHedge,
        root: {
          id: self.id,
          username: self.username,
          displayName: self.displayName,
          role: self.role,
          childCount: self.childCount,
        },
        basis,
      },
      ...state,
    },
    LATENCY.normal,
  )
}

/**
 * One level of the market's hierarchy tree: the DIRECT children of `parentId`
 * that hold a position on this market, agents and members alike.
 *
 * Mirrors `listChildren` so the tree expands the way the rest of the console
 * expands, and keeps the payload proportional to what is on screen. Wrapping in
 * `Projected` is not ceremony — an expanded branch is an exposure figure and has
 * to be able to go stale on its own row rather than borrowing the root's flag.
 *
 * A `parentId` outside the caller's subtree returns an EMPTY array, never an
 * error: absence and no-access stay indistinguishable.
 */
export async function getMarketBookBranch(
  marketId: string,
  parentId: string,
  opts: MarketBookOptions = {},
): Promise<Projected<BookBranchRow[]>> {
  const world = getWorld()
  const state = projectionState()
  const scope = scopeIds(world.session.accountId)
  if (!scope.has(parentId)) return delay({ value: [], ...state }, LATENCY.fast)

  const event = world.events.find((e) => e.markets.some((m) => m.id === marketId))
  const market = event?.markets.find((m) => m.id === marketId)
  if (!market) return delay({ value: [], ...state }, LATENCY.fast)

  const share = marketBasisShare(opts.basis ?? 'total')
  const marketBets = openBook(scope).filter((b) => b.marketId === marketId)
  const holders = new Set(marketBets.map((b) => b.memberId))
  const { view } = statusViewer(world)

  const rows: BookBranchRow[] = []
  for (const childId of world.children.get(parentId) ?? []) {
    const account = world.byId.get(childId)
    if (!account) continue
    const beneath = new Set(world.subtree.get(childId) ?? [childId])
    const own = marketBets.filter((b) => beneath.has(b.memberId))
    if (own.length === 0) continue

    const net = bookBySelection(own.map(legOf), market.selections)
    const positions: BookPosition[] = market.selections.map((name) => ({
      name,
      net: round2((net.get(name) ?? 0) * share),
    }))
    const values = positions.map((p) => p.net)

    rows.push({
      account: view(account),
      positions,
      worstCase: round2(Math.min(...values)),
      bestCase: round2(Math.max(...values)),
      betCount: own.length,
      memberCount: new Set(own.map((b) => b.memberId)).size,
      // Gates the expander. `childCount > 0` is not enough: a master with four
      // members none of whom bet this market must not offer an expander that
      // opens onto nothing.
      hasChildren: (world.children.get(childId) ?? []).some((grandChildId) =>
        (world.subtree.get(grandChildId) ?? [grandChildId]).some((id) => holders.has(id)),
      ),
    })
  }

  return delay({ value: rows.sort((a, b) => a.worstCase - b.worstCase), ...state }, LATENCY.fast)
}

/* ------------------------------------------------------------------- p&l -- */

export interface PnlQuery {
  range: DateRange
  rootId?: string
  memberIds?: string[]
  sports?: Sport[]
  games?: string[]
  page?: number
  pageSize?: number
  /** Client-only: ladder role for mapping live earnings → splitByRole. */
  myRole?: Role
}

export interface PnlSummary {
  rows: PnlRow[]
  total: number
  page: number
  pageSize: number
  totals: {
    turnover: number
    memberPnl: number
    commission: number
    /** The book's net, i.e. -(memberPnl - commission). */
    netPnl: number
    bets: number
    members: number
    /** netPnl / turnover — the margin the book actually held. */
    margin: number
  }
  daily: { date: string; netPnl: number; turnover: number; commission: number; bets: number }[]
  /** The signed-in account's slice of `netPnl`, by role, for the split chart. */
  splitByRole: { role: Role; amount: number }[]
  /** Live analytics freshness (optional). */
  asOf?: string
  martStale?: boolean
  /**
   * Present when analytics returns Unavailable for earnings (slice a6).
   * Never invent this from share_bp.
   */
  earningsUnavailable?: { code: string; reason: string; trackedBy?: string }
}

const emptyPnl = (page: number, pageSize: number): PnlSummary => ({
  rows: [],
  total: 0,
  page,
  pageSize,
  totals: { turnover: 0, memberPnl: 0, commission: 0, netPnl: 0, bets: 0, members: 0, margin: 0 },
  daily: [],
  splitByRole: [],
})

export async function getPnl(query: PnlQuery): Promise<PnlSummary> {
  const world = getWorld()
  const { range, memberIds, sports, games, page = 1, pageSize = 50 } = query
  const resolved = resolveRoot(query.rootId)
  if (!resolved) return delay(emptyPnl(page, pageSize), LATENCY.fast)
  const { scope } = resolved

  const rows = world.pnl
    .filter((r) => scope.has(r.memberId))
    .filter((r) => inRange(r.settledAt, range))
    .filter((r) => !memberIds?.length || memberIds.includes(r.memberId))
    .filter((r) => !sports?.length || sports.includes(r.sport))
    .filter((r) => !games?.length || games.includes(r.game))
    .sort((a, b) => b.settledAt.localeCompare(a.settledAt))

  const totals = rows.reduce(
    (acc, r) => {
      acc.turnover += r.turnover
      acc.memberPnl += r.memberPnl
      acc.commission += r.commission
      acc.netPnl -= r.netAmount
      acc.bets += r.betCount
      return acc
    },
    { turnover: 0, memberPnl: 0, commission: 0, netPnl: 0, bets: 0 },
  )

  const dailyMap = new Map<string, { date: string; netPnl: number; turnover: number; commission: number; bets: number }>()
  for (const r of rows) {
    const date = isoDay(r.settledAt)
    const entry = dailyMap.get(date) ?? { date, netPnl: 0, turnover: 0, commission: 0, bets: 0 }
    entry.netPnl -= r.netAmount
    entry.turnover += r.turnover
    entry.commission += r.commission
    entry.bets += r.betCount
    dailyMap.set(date, entry)
  }

  const roleMap = new Map<Role, number>()
  for (const r of rows) {
    for (const [role, amount] of Object.entries(r.split)) {
      if (typeof amount !== 'number') continue
      roleMap.set(role as Role, round2((roleMap.get(role as Role) ?? 0) + amount))
    }
  }

  const start = (page - 1) * pageSize
  return delay(
    {
      rows: rows.slice(start, start + pageSize),
      total: rows.length,
      page,
      pageSize,
      totals: {
        turnover: round2(totals.turnover),
        memberPnl: round2(totals.memberPnl),
        commission: round2(totals.commission),
        netPnl: round2(totals.netPnl),
        bets: totals.bets,
        members: new Set(rows.map((r) => r.memberId)).size,
        margin: totals.turnover > 0 ? totals.netPnl / totals.turnover : 0,
      },
      daily: [...dailyMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
      splitByRole: [...roleMap.entries()].map(([role, amount]) => ({ role, amount })),
    },
    LATENCY.slow,
  )
}

/** Per-member P&L leaderboard for the range. */
export async function getMemberPnl(query: PnlQuery): Promise<
  { account: Account; turnover: number; memberPnl: number; commission: number; netPnl: number; bets: number }[]
> {
  const world = getWorld()
  const resolved = resolveRoot(query.rootId)
  if (!resolved) return delay([], LATENCY.fast)
  const { scope } = resolved

  const map = new Map<string, { turnover: number; memberPnl: number; commission: number; netPnl: number; bets: number }>()
  for (const r of world.pnl) {
    if (!scope.has(r.memberId) || !inRange(r.settledAt, query.range)) continue
    if (query.sports?.length && !query.sports.includes(r.sport)) continue
    // `PnlQuery` has always carried `games`; this loop simply never read it, so
    // the game chip on Member P&L was decorative. One line, one real filter.
    if (query.games?.length && !query.games.includes(r.game)) continue
    if (query.memberIds?.length && !query.memberIds.includes(r.memberId)) continue
    const entry = map.get(r.memberId) ?? { turnover: 0, memberPnl: 0, commission: 0, netPnl: 0, bets: 0 }
    entry.turnover += r.turnover
    entry.memberPnl += r.memberPnl
    entry.commission += r.commission
    entry.netPnl -= r.netAmount
    entry.bets += r.betCount
    map.set(r.memberId, entry)
  }

  const { view } = statusViewer(world)
  return delay(
    [...map.entries()]
      .map(([id, v]) => ({
        account: view(world.byId.get(id)!),
        turnover: round2(v.turnover),
        memberPnl: round2(v.memberPnl),
        commission: round2(v.commission),
        netPnl: round2(v.netPnl),
        bets: v.bets,
      }))
      .filter((r) => r.account)
      .sort((a, b) => a.memberPnl - b.memberPnl),
    LATENCY.slow,
  )
}

/* -------------------------------------------------------------- member360 -- */

export interface MemberDetail {
  account: Account
  upline: Account[]
  daily: { date: string; turnover: number; memberPnl: number; bets: number }[]
  bySport: { sport: Sport; turnover: number; memberPnl: number; bets: number }[]
  /** 7 rows (Mon–Sun) x 24 columns of bet counts. */
  activity: number[][]
  stakeBuckets: { label: string; count: number }[]
  lifetime: {
    turnover: number
    memberPnl: number
    commission: number
    bets: number
    settledBets: number
    wins: number
    avgStake: number
    biggestWin: number
    biggestLoss: number
  }
  openExposure: number
}

export async function getMemberDetail(id: string, range: DateRange): Promise<MemberDetail | null> {
  const world = getWorld()
  const scope = scopeIds(world.session.accountId)
  if (!scope.has(id)) return delay(null, LATENCY.fast)
  const account = world.byId.get(id)
  if (!account) return delay(null, LATENCY.fast)

  const memberIds = new Set(world.subtree.get(id) ?? [id])
  const bets = world.bets.filter((b) => memberIds.has(b.memberId))
  const inWindow = bets.filter((b) => inRange(b.placedAt, range))
  const pnl = world.pnl.filter((r) => memberIds.has(r.memberId))
  const pnlInWindow = pnl.filter((r) => inRange(r.settledAt, range))

  const dailyMap = new Map<string, { date: string; turnover: number; memberPnl: number; bets: number }>()
  for (const b of inWindow) {
    const date = isoDay(b.placedAt)
    const entry = dailyMap.get(date) ?? { date, turnover: 0, memberPnl: 0, bets: 0 }
    entry.turnover += b.matched
    entry.bets += 1
    dailyMap.set(date, entry)
  }
  for (const r of pnlInWindow) {
    const date = isoDay(r.settledAt)
    const entry = dailyMap.get(date) ?? { date, turnover: 0, memberPnl: 0, bets: 0 }
    entry.memberPnl += r.memberPnl
    dailyMap.set(date, entry)
  }

  const sportMap = new Map<Sport, { turnover: number; memberPnl: number; bets: number }>()
  for (const b of inWindow) {
    const entry = sportMap.get(b.sport) ?? { turnover: 0, memberPnl: 0, bets: 0 }
    entry.turnover += b.matched
    entry.bets += 1
    sportMap.set(b.sport, entry)
  }
  for (const r of pnlInWindow) {
    const entry = sportMap.get(r.sport) ?? { turnover: 0, memberPnl: 0, bets: 0 }
    entry.memberPnl += r.memberPnl
    sportMap.set(r.sport, entry)
  }

  const activity = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
  for (const b of bets) {
    const d = new Date(b.placedAt)
    // Monday-first rows read better for a weekly rhythm than Sunday-first.
    const row = (d.getDay() + 6) % 7
    const col = d.getHours()
    activity[row]![col] = (activity[row]![col] ?? 0) + 1
  }

  const buckets = [
    { label: '<₹500', max: 500 },
    { label: '₹500–2k', max: 2_000 },
    { label: '₹2k–5k', max: 5_000 },
    { label: '₹5k–10k', max: 10_000 },
    { label: '₹10k–25k', max: 25_000 },
    { label: '₹25k+', max: Infinity },
  ]
  const stakeBuckets = buckets.map((b) => ({ label: b.label, count: 0 }))
  for (const b of bets) {
    const index = buckets.findIndex((bucket) => b.stake < bucket.max)
    const slot = stakeBuckets[index === -1 ? stakeBuckets.length - 1 : index]
    if (slot) slot.count += 1
  }

  const settled = bets.filter((b) => b.status === 'settled')
  const pnlValues = pnl.map((r) => r.memberPnl)

  const { view } = statusViewer(world)
  return delay(
    {
      account: view(account),
      upline: account.path
        .map((pid) => world.byId.get(pid))
        .filter((a): a is AccountRecord => Boolean(a))
        .map(view),
      daily: [...dailyMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
      bySport: [...sportMap.entries()]
        .map(([sport, v]) => ({ sport, ...v, turnover: round2(v.turnover), memberPnl: round2(v.memberPnl) }))
        .sort((a, b) => b.turnover - a.turnover),
      activity,
      stakeBuckets,
      lifetime: {
        turnover: round2(bets.reduce((s, b) => s + b.matched, 0)),
        memberPnl: round2(pnl.reduce((s, r) => s + r.memberPnl, 0)),
        commission: round2(pnl.reduce((s, r) => s + r.commission, 0)),
        bets: bets.length,
        settledBets: settled.length,
        wins: settled.filter((b) => b.result === 'won').length,
        avgStake: bets.length ? round2(bets.reduce((s, b) => s + b.stake, 0) / bets.length) : 0,
        biggestWin: pnlValues.length ? round2(Math.max(0, ...pnlValues)) : 0,
        biggestLoss: pnlValues.length ? round2(Math.min(0, ...pnlValues)) : 0,
      },
      openExposure: account.exposure,
    },
    LATENCY.slow,
  )
}

/* ------------------------------------------------------------------ bets -- */

export interface BetQuery {
  accountId?: string
  /** One market's log. The bet log is a RECORD, so unlike the exposure
   *  projection it can and does show settled, void and cancelled rows. */
  marketId?: string
  range?: DateRange
  statuses?: BetStatus[]
  sports?: Sport[]
  sides?: BetSide[]
  selections?: string[]
  /** Case-insensitive match on member username or display name. */
  search?: string
  page?: number
  pageSize?: number
}

/**
 * `Paged<Bet>` preserved by extension, so existing `Column<Bet>[]` tables keep
 * compiling untouched.
 *
 * `members` ships the identities for the rows on THIS page. Resolving them on
 * the client from some other payload is how a settled bet ends up with an em
 * dash in its Member column — the member is perfectly real, they just were not
 * in whichever open-book structure the page happened to have.
 */
export interface BetResult extends Paged<Bet> {
  /** Only the members present in `rows`. */
  members: Pick<Account, 'id' | 'username' | 'displayName' | 'role'>[]
}

export async function listBets(query: BetQuery = {}): Promise<BetResult> {
  const world = getWorld()
  const { marketId, range, statuses, sports, sides, selections, search = '', page = 1, pageSize = 50 } = query
  const resolved = resolveRoot(query.accountId)
  if (!resolved) return delay({ rows: [], total: 0, page, pageSize, members: [] }, LATENCY.fast)
  const { scope } = resolved
  const needle = search.trim().toLowerCase()

  const rows = world.bets
    .filter((b) => scope.has(b.memberId))
    .filter((b) => !marketId || b.marketId === marketId)
    .filter((b) => inRange(b.placedAt, range))
    .filter((b) => !statuses?.length || statuses.includes(b.status))
    .filter((b) => !sports?.length || sports.includes(b.sport))
    .filter((b) => !sides?.length || sides.includes(b.side))
    .filter((b) => !selections?.length || selections.includes(b.selection))
    .filter((b) => {
      if (!needle) return true
      const member = world.byId.get(b.memberId)
      if (!member) return false
      return (
        member.username.toLowerCase().includes(needle) ||
        member.displayName.toLowerCase().includes(needle)
      )
    })
    .sort((a, b) => b.placedAt.localeCompare(a.placedAt))

  const start = (page - 1) * pageSize
  const pageRows = rows.slice(start, start + pageSize)
  const members = [...new Set(pageRows.map((b) => b.memberId))]
    .map((id) => world.byId.get(id))
    .filter((a): a is Account => Boolean(a))
    .map((a) => ({ id: a.id, username: a.username, displayName: a.displayName, role: a.role }))

  return delay({ rows: pageRows, total: rows.length, page, pageSize, members }, LATENCY.normal)
}

/* ------------------------------------------------------------- transfers -- */

export interface TransferQuery {
  accountId?: string
  range?: DateRange
  direction?: 'give' | 'take'
  search?: string
  /**
   * Counterparty tier — the ONLY report where a role filter is real. Every P&L
   * row is member-grain by construction, so a tier chip there would offer one
   * option; a credit movement's counterparty can be any tier, and "show me only
   * movements with my Masters" is a real Monday question.
   */
  roles?: Role[]
  /** Exact counterparties from the picker. A substring over usernames is not a filter. */
  counterpartyIds?: string[]
  page?: number
  pageSize?: number
  /** Node Lab currency — hierarchy transfer minor units map with paiseToDisplay. */
  currencyType?: string
}

/**
 * The account on the other side of a movement from `rootId`.
 *
 * A downline-to-downline movement has neither side equal to the root, and the
 * receiving account is the one an operator means — the same account
 * `counterpartyOf` picks on the Transfers page. Keep the two in step or the
 * picker filters on one account while the table names another.
 */
const counterpartyIdOf = (t: Transfer, rootId: string): string =>
  t.fromId === rootId ? t.toId : t.toId === rootId ? t.fromId : t.toId

export interface TransferResult extends Paged<Transfer & { from: Account | null; to: Account | null }> {
  totals: { given: number; taken: number; net: number; count: number }
  daily: { date: string; given: number; taken: number }[]
  asOf?: string
  martStale?: boolean
  /**
   * When true, `balanceAfter*` are the target's live wallet balance (analytics
   * OpenAPI), not the historical post-movement ledger balances fixtures provide.
   */
  balanceAfterIsLive?: boolean
}

export async function listTransfers(query: TransferQuery = {}): Promise<TransferResult> {
  const world = getWorld()
  const { range, direction, search = '', roles, counterpartyIds, page = 1, pageSize = DEFAULT_PAGE_SIZE } = query
  const resolved = resolveRoot(query.accountId)
  if (!resolved) {
    return delay(
      {
        rows: [],
        total: 0,
        page,
        pageSize,
        totals: { given: 0, taken: 0, net: 0, count: 0 },
        daily: [],
      },
      LATENCY.fast,
    )
  }
  const { rootId, scope } = resolved
  const needle = search.trim().toLowerCase()

  // Counterparty filters run BEFORE `totals` and `daily` are reduced, so the
  // tiles and the chart describe the same filtered set as the table.
  const rows = world.transfers
    .filter((t) => scope.has(t.fromId) || scope.has(t.toId))
    .filter((t) => inRange(t.at, range))
    .filter((t) => {
      if (!direction) return true
      // Direction is relative to the account being viewed.
      return direction === 'give' ? t.fromId === rootId : t.toId === rootId
    })
    .filter((t) => !counterpartyIds?.length || counterpartyIds.includes(counterpartyIdOf(t, rootId)))
    .filter((t) => {
      if (!roles?.length) return true
      const other = world.byId.get(counterpartyIdOf(t, rootId))
      return Boolean(other && roles.includes(other.role))
    })
    .filter((t) => {
      if (!needle) return true
      const from = world.byId.get(t.fromId)?.username ?? ''
      const to = world.byId.get(t.toId)?.username ?? ''
      return (
        from.toLowerCase().includes(needle) ||
        to.toLowerCase().includes(needle) ||
        t.ref.toLowerCase().includes(needle) ||
        t.remark.toLowerCase().includes(needle)
      )
    })
    .sort((a, b) => b.at.localeCompare(a.at))

  const { viewOrNull } = statusViewer(world)
  const totals = rows.reduce(
    (acc, t) => {
      if (t.fromId === rootId) acc.given += t.amount
      else if (t.toId === rootId) acc.taken += t.amount
      acc.count += 1
      return acc
    },
    { given: 0, taken: 0, net: 0, count: 0 },
  )
  totals.net = round2(totals.taken - totals.given)

  const dailyMap = new Map<string, { date: string; given: number; taken: number }>()
  for (const t of rows) {
    const date = isoDay(t.at)
    const entry = dailyMap.get(date) ?? { date, given: 0, taken: 0 }
    if (t.fromId === rootId) entry.given += t.amount
    else if (t.toId === rootId) entry.taken += t.amount
    dailyMap.set(date, entry)
  }

  const start = (page - 1) * pageSize
  return delay(
    {
      rows: rows.slice(start, start + pageSize).map((t) => ({
        ...t,
        from: viewOrNull(world.byId.get(t.fromId)),
        to: viewOrNull(world.byId.get(t.toId)),
      })),
      total: rows.length,
      page,
      pageSize,
      totals: {
        given: round2(totals.given),
        taken: round2(totals.taken),
        net: totals.net,
        count: totals.count,
      },
      daily: [...dailyMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
    },
    LATENCY.normal,
  )
}

/**
 * The account statement — every movement for one account, running balance
 * included, ordered oldest first so the closing balance is the last row.
 *
 * The closing balance MUST equal the account's balance; that reconciliation is
 * an acceptance criterion, and `statementReconciles` below is what proves it.
 */
export interface StatementRow {
  id: string
  at: string
  kind: 'credit-in' | 'credit-out' | 'settlement'
  description: string
  counterparty: string | null
  amount: number
  /** Running ledger balance after this movement (same as balanceAfter). */
  balance: number
  /** Balance immediately before this movement. Null when unknown. */
  balanceBefore: number | null
  /** Balance immediately after this movement. */
  balanceAfter: number
  ref: string
}

export async function getStatement(
  accountId: string,
  range?: DateRange,
): Promise<{ rows: StatementRow[]; openingBalance: number; closingBalance: number }> {
  const world = getWorld()
  const scope = scopeIds(world.session.accountId)
  if (!scope.has(accountId)) {
    return delay({ rows: [], openingBalance: 0, closingBalance: 0 }, LATENCY.fast)
  }

  const account = world.byId.get(accountId)!
  const memberIds = new Set(world.subtree.get(accountId) ?? [accountId])

  type Movement = { at: string; row: Omit<StatementRow, 'balance' | 'balanceBefore' | 'balanceAfter'> }
  const movements: Movement[] = []

  for (const t of world.transfers) {
    if (t.toId === accountId) {
      movements.push({
        at: t.at,
        row: {
          id: t.id,
          at: t.at,
          kind: 'credit-in',
          description: t.remark || 'Credit received',
          counterparty: world.byId.get(t.fromId)?.username ?? null,
          amount: t.amount,
          ref: t.ref,
        },
      })
    } else if (t.fromId === accountId) {
      movements.push({
        at: t.at,
        row: {
          id: t.id,
          at: t.at,
          kind: 'credit-out',
          description: t.remark || 'Credit sent',
          counterparty: world.byId.get(t.toId)?.username ?? null,
          amount: -t.amount,
          ref: t.ref,
        },
      })
    }
  }

  for (const r of world.pnl) {
    if (r.memberId === accountId) {
      movements.push({
        at: r.settledAt,
        row: {
          id: r.id,
          at: r.settledAt,
          kind: 'settlement',
          description: `${r.eventName} — ${r.marketName}`,
          counterparty: null,
          amount: r.netAmount,
          ref: r.id.replace('pnl-', ''),
        },
      })
    } else if (memberIds.has(r.memberId)) {
      const amount = r.split[account.role]
      if (typeof amount === 'number' && amount !== 0) {
        movements.push({
          at: r.settledAt,
          row: {
            id: `${r.id}-${account.id}`,
            at: r.settledAt,
            kind: 'settlement',
            description: `Share — ${r.eventName}`,
            counterparty: world.byId.get(r.memberId)?.username ?? null,
            amount,
            ref: r.id.replace('pnl-', ''),
          },
        })
      }
    }
  }

  movements.sort((a, b) => a.at.localeCompare(b.at))

  const seed = account.parentId === null ? 80_000_000 : 0
  let running = seed
  const all: StatementRow[] = movements.map((m) => {
    const balanceBefore = running
    running = round2(running + m.row.amount)
    return {
      ...m.row,
      balanceBefore,
      balanceAfter: running,
      balance: running,
    }
  })

  const windowed = range ? all.filter((r) => inRange(r.at, range)) : all
  const openingBalance = windowed.length
    ? round2((windowed[0]!.balance ?? 0) - (windowed[0]!.amount ?? 0))
    : running

  return delay(
    {
      rows: windowed.reverse(),
      openingBalance,
      closingBalance: running,
    },
    LATENCY.normal,
  )
}

/* -------------------------------------------------------------- activity -- */

export async function listActivity(limit = 60): Promise<(ActivityEvent & { actor: Account | null; subject: Account | null })[]> {
  const world = getWorld()
  const scope = scopeIds(world.session.accountId)
  const { viewOrNull } = statusViewer(world)
  return delay(
    world.activity
      .filter((a) => scope.has(a.actorId) || scope.has(a.subjectId))
      .slice(0, limit)
      .map((a) => ({
        ...a,
        actor: viewOrNull(world.byId.get(a.actorId)),
        subject: viewOrNull(world.byId.get(a.subjectId)),
      })),
    LATENCY.normal,
  )
}

/* ------------------------------------------------------------- mutations -- */

export interface TransferInput {
  accountId: string
  amount: number
  direction: 'give' | 'take'
  remark: string
  /** One key per opened dialog — replaying it must move money once. */
  idempotencyKey: string
  /** Node currency — POINTS amounts convert 1 point = ₹100 to paise. */
  currencyType?: 'INR' | 'POINTS' | 'POINT@1'
}

const appliedKeys = new Map<string, Transfer>()

/**
 * Move credit between the signed-in account and one of its direct children.
 *
 * Deliberately NOT optimistic. It resolves with both reconciled balances and
 * the caller refetches; nothing on screen shows a money value the ledger has
 * not confirmed (acceptance: "Every money value on screen is a ledger
 * projection; no optimistic write is ever rendered as settled").
 */
export interface TransferResultRow {
  transfer: Transfer
  from: Account
  to: Account
  /**
   * The CHILD's settlement position with the caller AFTER the move. 0 = settled.
   *
   * On the mutation result and deliberately NOT on `Transfer`: a transfer is a
   * ledger record of a movement, a settlement is a position. It is returned
   * explicitly rather than left to the caller to work out, so a bulk report does
   * not have to guess which of `from` / `to` was the child.
   */
  settlementAfter: number
}

export async function transferCredit(input: TransferInput): Promise<TransferResultRow> {
  const world = getWorld()

  const existing = appliedKeys.get(input.idempotencyKey)
  if (existing) {
    // Replay: same result, no second movement. Recompute `settlementAfter` from
    // the stored child so a retry reports the same settled/outstanding figure
    // the first attempt did.
    const child = world.byId.get(existing.direction === 'give' ? existing.toId : existing.fromId)
    const { view } = statusViewer(world)
    return delay(
      {
        transfer: existing,
        from: view(world.byId.get(existing.fromId)!),
        to: view(world.byId.get(existing.toId)!),
        settlementAfter: child?.settlement ?? 0,
      },
      LATENCY.fast,
    )
  }

  const self = world.byId.get(world.session.accountId)
  const other = world.byId.get(input.accountId)
  if (!self || !other) throw new Error('Account not found in your downline')
  if (other.parentId !== self.id) throw new Error('You can only move credit with your direct downline')
  if (!(input.amount > 0)) throw new Error('Enter an amount greater than zero')

  const giving = input.direction === 'give'
  const source = giving ? self : other
  const target = giving ? other : self

  // Lab / Agent-API: settle outstanding Take/Give via posted P&L first — no TB
  // cash required when amount ≤ |child.settlement| in the matching direction.
  const settleRoom =
    giving && other.settlement < 0
      ? round2(-other.settlement)
      : !giving && other.settlement > 0
        ? round2(other.settlement)
        : 0
  const pnlAmount = Math.min(input.amount, Math.max(0, settleRoom))
  const cashAmount = round2(input.amount - pnlAmount)

  if (cashAmount > 0 && source.balance < cashAmount) {
    throw new Error(
      `${source.username} only has ${source.balance.toFixed(2)} available — that is less than the amount requested`,
    )
  }
  // Give/Take: no child creditLimit headroom on Give.

  if (cashAmount > 0) {
    source.balance = round2(source.balance - cashAmount)
    target.balance = round2(target.balance + cashAmount)
  }

  // Settlement leg: Take reduces child.settlement; Give raises it toward zero.
  other.settlement = round2(other.settlement + (giving ? input.amount : -input.amount))

  const transfer: Transfer = {
    id: `txn-live-${appliedKeys.size + 1}`,
    ref: `SX${String(900000 + appliedKeys.size + 1)}`,
    at: new Date().toISOString(),
    fromId: source.id,
    toId: target.id,
    amount: round2(input.amount),
    direction: input.direction,
    remark: input.remark,
    balanceAfterFrom: source.balance,
    balanceAfterTo: target.balance,
    byId: self.id,
  }
  world.transfers.push(transfer)
  appliedKeys.set(input.idempotencyKey, transfer)

  const { view } = statusViewer(world)
  return delay(
    { transfer, from: view(source), to: view(target), settlementAfter: other.settlement },
    LATENCY.slow,
  )
}

/* ---------------------------------------------------------- bulk settle -- */

export interface BulkSettleInput {
  /** Direct children to settle to zero. Anything else is reported, not moved. */
  accountIds: string[]
  /** ONE key per run. Row keys are derived from it deterministically. */
  idempotencyKey: string
  remark?: string
}

export interface BulkSettleRow {
  accountId: string
  username: string
  direction: 'give' | 'take'
  /** |settlement| at the moment the row was reached. */
  wanted: number
  moved: number
  /** Still outstanding after the move. 0 on a fully settled row. */
  remaining: number
  outcome: 'settled' | 'partial' | 'skipped'
  /** Present on every row that did not settle in full. Names the actual constraint. */
  reason?: string
  ref?: string
}

export interface BulkSettleReport {
  /** In EXECUTION order, so the report reads as the sequence that actually ran. */
  rows: BulkSettleRow[]
  totals: {
    collected: number
    paid: number
    net: number
    settled: number
    partial: number
    skipped: number
  }
}

/**
 * The sentence naming the cap that bit, or null when the full amount can move.
 *
 * Exported because THREE surfaces quote it — the plan table, the live run and
 * the report — and a cap explained one way in the plan and another in the report
 * is a support ticket. Never the bare word "capped": always which constraint,
 * and always with the figure, because the operator's next move depends on which
 * one it was.
 */
export function settlementCapReason(
  account: Pick<Account, 'username' | 'balance' | 'creditLimit' | 'currencyType'>,
  plan: SettlementPlan,
  self: Pick<Account, 'balance' | 'exposure' | 'currencyType'>,
  freeToGive: number = availableToGive(self.balance, self.exposure),
): string | null {
  switch (plan.capped) {
    case 'their-balance':
      return `capped by their ${money(account.balance, { currency: account.currencyType, alreadyDisplay: true })} balance`
    case 'your-free':
      return `capped by your ${money(freeToGive, { currency: self.currencyType, alreadyDisplay: true })} available`
    default:
      return null
  }
}

/** Why a row will not move at all. A zero-amount transfer is not a movement. */
export function settlementSkipReason(
  account: Pick<Account, 'username' | 'balance' | 'creditLimit' | 'currencyType'>,
  plan: SettlementPlan,
  self: Pick<Account, 'balance' | 'exposure' | 'currencyType'>,
  freeToGive: number = availableToGive(self.balance, self.exposure),
): string {
  if (plan.direction === 'take') {
    return `${account.username} holds ${money(account.balance, { currency: account.currencyType, alreadyDisplay: true })}, so there is nothing to take`
  }
  return `you have ${money(freeToGive, { currency: self.currencyType, alreadyDisplay: true })} available — your ${money(self.balance, { currency: self.currencyType, alreadyDisplay: true })} balance is committed to open bets`
}

/** Deterministic per-row key. Replaying a run replays each row, never doubles it. */
export const bulkSettleRowKey = (
  runKey: string,
  accountId: string,
  direction: 'give' | 'take',
  amount: number,
): string =>
  // Amount is in the key, in paise, so a retry that legitimately moves a SMALLER
  // amount (because a cap shrank between attempts) gets a different key and is a
  // genuine second movement. Direction is in the key so a plan carrying both a
  // take and a give on one account cannot collide — impossible today, since
  // settlement has one sign, but the key must not depend on that staying true.
  `${runKey}:${accountId}:${direction}:${Math.round(amount * 100)}`

/**
 * Settle a set of direct children to zero in one run.
 *
 * SEQUENTIAL, never `Promise.all`. Two reasons, both load-bearing: each row's
 * cap depends on what the previous row did to the caller's balance — giving to
 * one child must reduce the free-to-give available to the next, or a run can
 * overdraw the parent — and a parallel burst against a ledger leaves no
 * defensible ordering when one of them fails.
 *
 * Ordering is takes first (largest first), then gives smallest-first:
 *   · A take RAISES the caller's free-to-give, so running takes first strictly
 *     increases how much of the give side can complete. The other order leaves
 *     gives short for money sitting in the very accounts about to be swept.
 *   · When free-to-give runs out, ascending gives maximise the NUMBER of rows
 *     that close in full and land the whole shortfall on one large row. Eleven
 *     closed rows and one to chase beats twelve rows all still open.
 *
 * PARTIAL BY POLICY. A row that cannot move its full amount moves what it can
 * and reports the remainder. Refusing to move anything because the whole cannot
 * move is the behaviour operators work around by hand.
 *
 * Nothing here is optimistic: every figure in the report is quoted from a
 * mutation result, never from what the plan predicted.
 */
export async function bulkSettle(input: BulkSettleInput): Promise<BulkSettleReport> {
  const world = getWorld()
  const self = world.byId.get(world.session.accountId)!

  interface Candidate {
    // The stored row: this loop only reads money off it and never returns it, so
    // it has no business paying for the status projection on every candidate.
    account: AccountRecord
    plan: ReturnType<typeof settlementPlan>
  }

  const rows: BulkSettleRow[] = []
  const runnable: Candidate[] = []

  for (const id of input.accountIds) {
    const account = world.byId.get(id)
    if (!account || account.parentId !== self.id) {
      rows.push({
        accountId: id,
        username: account?.username ?? id,
        direction: 'take',
        wanted: 0,
        moved: 0,
        remaining: account ? Math.abs(round2(account.settlement)) : 0,
        outcome: 'skipped',
        reason: account
          ? `${account.username} sits further down — credit moves only between you and your direct children`
          : 'That account is not in your downline',
      })
      continue
    }
    const plan = settlementPlan(account, self)
    if (!plan) {
      rows.push({
        accountId: id,
        username: account.username,
        direction: 'take',
        wanted: 0,
        moved: 0,
        remaining: 0,
        outcome: 'skipped',
        reason: `${account.username} has nothing outstanding to settle`,
      })
      continue
    }
    runnable.push({ account, plan })
  }

  const takes = runnable
    .filter((c) => c.plan!.direction === 'take')
    .sort((a, b) => b.plan!.wanted - a.plan!.wanted)
  const gives = runnable
    .filter((c) => c.plan!.direction === 'give')
    .sort((a, b) => a.plan!.wanted - b.plan!.wanted)

  for (const { account } of [...takes, ...gives]) {
    // Re-plan against the LIVE world rather than the figure computed when the
    // run was assembled: the previous row has already changed `self.balance`,
    // and on a give-heavy run that is exactly the difference between a clean
    // partial and a rejected movement.
    const live = settlementPlan(account, self)
    if (!live) {
      rows.push({
        accountId: account.id,
        username: account.username,
        direction: 'take',
        wanted: 0,
        moved: 0,
        remaining: 0,
        outcome: 'skipped',
        reason: `${account.username} settled before this row was reached`,
      })
      continue
    }

    if (live.possible <= 0) {
      rows.push({
        accountId: account.id,
        username: account.username,
        direction: live.direction,
        wanted: live.wanted,
        moved: 0,
        remaining: live.wanted,
        outcome: 'skipped',
        reason: settlementSkipReason(account, live, self),
      })
      continue
    }

    try {
      const result = await transferCredit({
        accountId: account.id,
        amount: live.possible,
        direction: live.direction,
        remark: input.remark ?? 'Bulk settlement',
        idempotencyKey: bulkSettleRowKey(
          input.idempotencyKey,
          account.id,
          live.direction,
          live.possible,
        ),
      })
      const remaining = Math.abs(round2(result.settlementAfter))
      rows.push({
        accountId: account.id,
        username: account.username,
        direction: live.direction,
        wanted: live.wanted,
        moved: round2(result.transfer.amount),
        remaining,
        outcome: remaining === 0 ? 'settled' : 'partial',
        ...(remaining === 0
          ? {}
          : { reason: settlementCapReason(account, live, self) ?? 'partly settled' }),
        ref: result.transfer.ref,
      })
    } catch (error) {
      // A rejected movement reports as `skipped` carrying the LEDGER's own
      // message, not a paraphrase. One member being over their limit must not
      // stop Monday, so the loop continues.
      rows.push({
        accountId: account.id,
        username: account.username,
        direction: live.direction,
        wanted: live.wanted,
        moved: 0,
        remaining: live.wanted,
        outcome: 'skipped',
        reason: error instanceof Error ? error.message : 'The ledger rejected that movement',
      })
    }
  }

  const totals = rows.reduce(
    (acc, row) => {
      if (row.direction === 'take') acc.collected = round2(acc.collected + row.moved)
      else acc.paid = round2(acc.paid + row.moved)
      acc[row.outcome] += 1
      return acc
    },
    { collected: 0, paid: 0, net: 0, settled: 0, partial: 0, skipped: 0 },
  )
  totals.net = round2(totals.collected - totals.paid)

  return { rows, totals }
}

export interface CreateAccountInput {
  role: Role
  username: string
  displayName: string
  creditLimit: number
  /** Lab currency. Only CM→TM create may choose; else inherited. */
  currencyType?: 'INR' | 'POINTS' | 'POINT@1'
  /** Lab casinoCurrencyType — Evolution HKD|AED (CM→TM). */
  casinoCurrencyType?: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — Crash Games (CM→TM). */
  fundistCurrencyType?: 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'
  /** Sportsbook partnership share (percentage points). */
  share: number
  /** Casino partnership share (percentage points). */
  shareCasino: number
  commission: { fancy: number; bookmaker: number; matchOdds: number }
  betLimits: { min: number; max: number }
  games: Account['games']
  comments?: string
  /** Collected for future provisioning (#149); fixture does not store plaintext. */
  password?: string
  /**
   * Max credit this agent may assign to each level beneath them.
   * Winner7 Create Member cascade — persisted on hierarchy as downlineMaxCredit.
   */
  downlineMaxCredit?: Partial<Record<Role, number>>
}

export async function createAccount(input: CreateAccountInput): Promise<Account> {
  const world = getWorld()
  const parent = world.byId.get(world.session.accountId)!

  const allowed = creatableRoles(parent.role)
  if (!allowed.includes(input.role)) {
    throw new Error(
      `As a ${ROLE_LABEL[parent.role]} you can only create ${allowed.map((r) => ROLE_LABEL[r]).join(' or ')} — not ${ROLE_LABEL[input.role]}`,
    )
  }

  if (world.accounts.some((a) => a.username.toLowerCase() === input.username.toLowerCase())) {
    throw new Error('That username is already taken')
  }

  // Max assignable = remaining credit line, not ledger cash.
  const directKids = world.children.get(parent.id) ?? []
  const allocated = directKids.reduce((sum, id) => sum + (world.byId.get(id)?.creditLimit ?? 0), 0)
  const availableCredit = Math.max(0, parent.creditLimit - allocated)
  const roleCap = parent.downlineMaxCredit?.[input.role]
  const maxForRole =
    roleCap != null && Number.isFinite(roleCap)
      ? Math.min(availableCredit, roleCap)
      : availableCredit
  if (input.creditLimit > maxForRole) {
    throw new Error(
      `Credit limit cannot exceed the ${maxForRole.toFixed(2)} credit you still have to assign`,
    )
  }

  if (input.downlineMaxCredit) {
    for (const [role, amount] of Object.entries(input.downlineMaxCredit)) {
      if (amount == null || !Number.isFinite(amount)) continue
      const parentCap = parent.downlineMaxCredit?.[role as Role]
      const ceiling =
        parentCap != null && Number.isFinite(parentCap)
          ? Math.min(input.creditLimit, parentCap)
          : input.creditLimit
      if (amount > ceiling) {
        throw new Error(
          `${ROLE_LABEL[role as Role]} max credit cannot exceed ${ceiling.toFixed(2)}`,
        )
      }
    }
  }

  const account: AccountRecord = {
    id: `acc-new-${world.accounts.length + 1}`,
    username: input.username,
    displayName: input.displayName || input.username,
    role: input.role,
    parentId: parent.id,
    path: [...parent.path, parent.id],
    status: 'active',
    createdAt: new Date().toISOString(),
    lastActiveAt: null,
    creditLimit: input.creditLimit,
    ...(input.downlineMaxCredit && Object.keys(input.downlineMaxCredit).length > 0
      ? { downlineMaxCredit: { ...input.downlineMaxCredit } }
      : {}),
    // Opening balance equals the assigned credit limit.
    balance: input.creditLimit,
    distributedBalance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: input.share, casino: input.shareCasino },
    commission: input.commission,
    betLimits: input.betLimits,
    games: input.games,
    childCount: 0,
    memberCount: 0,
    // Account provisioning does not exist yet (#149) — the record is real, the
    // login is not, and the UI must say so rather than imply a working signup.
    flags: ['no-login-yet'],
    comments: input.comments,
  }

  world.accounts.push(account)
  world.byId.set(account.id, account)
  world.children.set(parent.id, [...(world.children.get(parent.id) ?? []), account.id])
  for (const ancestor of [parent.id, ...parent.path]) {
    world.subtree.set(ancestor, [...(world.subtree.get(ancestor) ?? []), account.id])
  }
  world.subtree.set(account.id, [account.id])
  parent.childCount += 1

  return delay(statusViewer(world).view(account), LATENCY.slow)
}

/**
 * Update a direct child. Username and role stay locked; blank password leaves
 * the existing one alone.
 */
export interface UpdateAccountInput {
  accountId: string
  displayName: string
  status: Account['status']
  creditLimit: number
  share: number
  shareCasino: number
  commission: { fancy: number; bookmaker: number; matchOdds: number }
  betLimits: { min: number; max: number }
  games: Account['games']
  comments?: string
  /** When set, replaces the Keycloak password later; fixture only records intent. */
  password?: string
  /** Child Lab currency — required so POINTS credit 20 PATCHes 200000 paise. */
  currencyType?: Account['currencyType']
  /** Lab casinoCurrencyType — Evolution HKD|AED (CM edit TM). */
  casinoCurrencyType?: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — Crash Games (CM edit TM). */
  fundistCurrencyType?: 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'
}

export async function updateAccount(input: UpdateAccountInput): Promise<Account> {
  const world = getWorld()
  const parent = world.byId.get(world.session.accountId)!
  const subject = world.byId.get(input.accountId)

  if (!subject || subject.parentId !== parent.id) {
    throw new Error('You can only edit your direct downline')
  }
  if (subject.status === 'closed') {
    throw new Error('Closed accounts cannot be edited')
  }

  const siblings = (world.children.get(parent.id) ?? []).filter((id) => id !== subject.id)
  const allocated = siblings.reduce((sum, id) => sum + (world.byId.get(id)?.creditLimit ?? 0), 0)
  const availableCredit = Math.max(0, parent.creditLimit - allocated)
  if (input.creditLimit > availableCredit) {
    throw new Error(
      `Credit limit cannot exceed the ${availableCredit.toFixed(2)} credit you still have to assign`,
    )
  }

  // Credit Limit should be greater than Balance Up.
  const balanceUpFloor = subject.settlement > 0 ? subject.settlement : 0
  if (balanceUpFloor > 0 && input.creditLimit < balanceUpFloor) {
    throw new Error(`Credit Limit should be greater than Balance Up (${balanceUpFloor.toFixed(2)})`)
  }

  const previousCredit = subject.creditLimit
  subject.displayName = input.displayName.trim() || subject.username
  subject.status = input.status
  subject.creditLimit = input.creditLimit
  subject.share = { sportsbook: input.share, casino: input.shareCasino }
  subject.commission = input.commission
  subject.betLimits = input.betLimits
  subject.games = input.games
  subject.comments = input.comments

  // Mirror create: raising the line also raises opening-style balance headroom
  // by the delta (cash already moved via Give/Take stays as-is below the ceiling).
  if (input.creditLimit > previousCredit) {
    const delta = input.creditLimit - previousCredit
    subject.balance = round2(subject.balance + delta)
  } else if (input.creditLimit < previousCredit && subject.balance > input.creditLimit) {
    subject.balance = input.creditLimit
  }

  if (input.status !== 'suspended') {
    suspensions(world).delete(subject.id)
  }

  return delay(statusViewer(world).view(subject), LATENCY.slow)
}

/** Usernames already taken, for inline availability checks on the create form. */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  const world = getWorld()
  const taken = world.accounts.some((a) => a.username.toLowerCase() === username.trim().toLowerCase())
  return delay(!taken, LATENCY.fast)
}

/* ---------------------------------------------------------------- search -- */

export interface SearchQuery {
  text: string
  /** Cap per group. Default 5. */
  limitPerKind?: number
  /** Restrict which kinds are searched. `action` is never served here. */
  kinds?: SearchKind[]
  /** Scopes every kind. Defaults to the signed-in account. */
  rootId?: string
}

const SEARCH_GROUP_LABEL: Record<SearchKind, string> = {
  account: 'Agents & members',
  market: 'Live book',
  transfer: 'Transfers',
  action: 'Actions',
}

/**
 * The kinds this function serves, in their tie-break order.
 *
 * `action` is absent on purpose: actions and pages are matched client-side so
 * they appear on the FIRST keystroke regardless of network latency. Serving them
 * from here would mean the most predictable rows in the palette are the slowest
 * to arrive.
 */
const SEARCH_DATA_KINDS: SearchKind[] = ['account', 'market', 'transfer']

const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

/**
 * The best of several fields for one entity, with the highlight ranges mapped
 * back onto the rendered TITLE.
 *
 * `titleOffset` is where that field's text begins inside the title. Omit it for
 * a field that is not part of the title at all — a display-name hit still
 * ranks the row, but it must not paint marks at offsets that mean something
 * different in the string actually on screen.
 */
function bestFieldMatch(
  kind: SearchKind,
  needle: string,
  fields: { text: string; weight: FieldWeight; titleOffset?: number }[],
): { score: number; ranges: [number, number][] } | null {
  let best: { score: number; ranges: [number, number][] } | null = null
  for (const field of fields) {
    if (!field.text) continue
    const match: FieldMatch | null = matchField(field.text, needle)
    if (!match) continue
    const score = scoreMatch(kind, match, field.weight)
    if (best && score <= best.score) continue
    const offset = field.titleOffset
    best = {
      score,
      ranges:
        offset === undefined
          ? []
          : match.ranges.map(([s, e]) => [s + offset, e + offset] as [number, number]),
    }
  }
  return best
}

/**
 * One ranked, grouped read across everything the console can be searched for.
 *
 * Scoped by `resolveRoot` exactly like every other read: an account, market or
 * transfer outside your subtree produces NO hit, never a "forbidden" — the same
 * rule `getAccount` follows, because a denial confirms the thing exists.
 *
 * Markets come from the OPEN book only, which is what makes runner names
 * searchable at all: a settled market has no position to land on, and returning
 * one would send the operator to a page with nothing on it. The no-results copy
 * says so out loud rather than leaving the gap for them to discover.
 *
 * Live swap target: `GET /v1/search?q=&kinds=&limit=`.
 */
export async function searchConsole(query: SearchQuery): Promise<SearchResult> {
  const world = getWorld()
  const needle = query.text.trim().toLowerCase()
  const limitPerKind = query.limitPerKind ?? 5
  const kinds = (query.kinds ?? SEARCH_DATA_KINDS).filter((k) => k !== 'action')
  const projection = projectionState()

  const empty = (): SearchResult => ({
    text: query.text,
    groups: kinds.map((kind) => ({
      kind,
      label: SEARCH_GROUP_LABEL[kind],
      // Empty arrays, never undefined: "no match" and "this kind was not
      // searched" are different facts and the palette renders them differently.
      hits: [],
      total: 0,
    })),
    exposureUnavailable: projection.stale,
  })

  const resolved = resolveRoot(query.rootId)
  if (!needle || !resolved) return delay(empty(), LATENCY.fast)
  const { rootId, scope } = resolved

  const byKind: Record<SearchKind, SearchHit[]> = {
    account: [],
    market: [],
    transfer: [],
    action: [],
  }

  /* ---- agents & members ---- */
  if (kinds.includes('account')) {
    const { view } = statusViewer(world)
    for (const id of scope) {
      if (id === rootId) continue
      const a = world.byId.get(id)
      if (!a) continue
      const match = bestFieldMatch('account', needle, [
        { text: a.username, weight: SEARCH_SCORE.weight.primary, titleOffset: 0 },
        { text: a.displayName, weight: SEARCH_SCORE.weight.secondary },
      ])
      if (!match) continue

      const parent = a.parentId ? world.byId.get(a.parentId) : null
      const activeToday =
        a.lastActiveAt !== null && world.now - Date.parse(a.lastActiveAt) < 86_400_000
      const viewed = view(a)
      const stop: StopState = {
        effectiveStatus: viewed.effectiveStatus,
        stoppedBy: viewed.stoppedBy,
        stopControl: viewed.stopControl,
      }

      byKind.account.push({
        id: `account:${a.id}`,
        kind: 'account',
        title: a.username,
        subtitle:
          a.role === 'player'
            ? `${a.displayName} · ${ROLE_LABEL.player}${parent ? ` · under ${parent.username}` : ''}`
            : `${a.displayName} · ${ROLE_LABEL[a.role]} · ${count(a.memberCount)} members`,
        meta: a.lastActiveAt ? ago(a.lastActiveAt, world.now) : 'never signed in',
        ranges: match.ranges,
        to: `/member/${a.id}`,
        icon: a.role === 'player' ? 'user' : 'users',
        score: match.score + (activeToday ? SEARCH_SCORE.live.activeToday : 0),
        // Both: `role` drives the tier-coloured RoleChip the palette renders,
        // `badge` stays as the text fallback and is what the row's accessible
        // name is built from.
        role: a.role,
        badge: { label: ROLE_SHORT[a.role], tone: 'muted' },
        // Landing an operator on a stopped account without saying so is the
        // fifth screen that forgot; the palette gets the same projection a row
        // does.
        stop,
      })
    }
  }

  /* ---- the live book: events, markets, runners ---- */
  // Skipped entirely when the projection is stale. A palette row has no space
  // for the unavailability banner, so the honest move is to say nothing rather
  // than show a figure that describes a book which has moved on.
  if (kinds.includes('market') && !projection.stale) {
    for (const [eventId, eventBets] of betsByEvent(openBook(scope))) {
      const event = world.eventById.get(eventId)
      if (!event) continue
      // Always the TOTAL book. A search result must not silently depend on a
      // My-share lens the palette does not show and cannot explain.
      const rolled = buildEventExposure(event, eventBets, 1)
      if (!rolled) continue
      const liveBonus = rolled.inPlay ? SEARCH_SCORE.live.inPlay : 0

      const eventMatch = bestFieldMatch('market', needle, [
        { text: rolled.eventName, weight: SEARCH_SCORE.weight.secondary, titleOffset: 0 },
      ])
      if (eventMatch) {
        byKind.market.push({
          id: `event:${eventId}`,
          kind: 'market',
          title: rolled.eventName,
          subtitle: `${rolled.sport} · ${count(rolled.betCount)} bets · ${count(rolled.memberCount)} members`,
          meta: rolled.inPlay ? 'In play' : lll(rolled.startsAt),
          ranges: eventMatch.ranges,
          // No event detail page exists in this console, so an event result
          // lands on the exposure grid focused on that event.
          to: `/exposure?event=${encodeURIComponent(eventId)}`,
          icon: rolled.inPlay ? 'live' : 'exposure',
          score: eventMatch.score + liveBonus,
          badge: { label: money(rolled.worstCase, { round: true }), tone: toneOf(rolled.worstCase) },
        })
      }

      for (const market of rolled.markets) {
        const title = `${rolled.eventName} — ${market.marketName}`
        // Only the market NAME is matched here. The event name already produced
        // its own row above; matching it again on every market would fill the
        // group with near-duplicates of one answer.
        const marketMatch = bestFieldMatch('market', needle, [
          {
            text: market.marketName,
            weight: SEARCH_SCORE.weight.tertiary,
            titleOffset: rolled.eventName.length + 3,
          },
        ])
        if (marketMatch) {
          byKind.market.push({
            id: `market:${market.marketId}`,
            kind: 'market',
            title,
            subtitle: `${market.marketType} · ${rolled.sport} · ${count(market.betCount)} bets`,
            meta: rolled.inPlay ? 'In play' : lll(rolled.startsAt),
            ranges: marketMatch.ranges,
            to: `/exposure/market/${market.marketId}`,
            icon: rolled.inPlay ? 'live' : 'exposure',
            score: marketMatch.score + liveBonus,
            badge: {
              label: money(market.worstCase, { round: true }),
              tone: toneOf(market.worstCase),
            },
          })
        }

        for (const selection of market.selections) {
          const selectionMatch = bestFieldMatch('market', needle, [
            { text: selection.name, weight: SEARCH_SCORE.weight.tertiary, titleOffset: 0 },
          ])
          if (!selectionMatch) continue
          byKind.market.push({
            id: `selection:${market.marketId}:${selection.name}`,
            kind: 'market',
            title: selection.name,
            subtitle: title,
            meta: rolled.inPlay ? 'In play' : lll(rolled.startsAt),
            ranges: selectionMatch.ranges,
            to: `/exposure/market/${market.marketId}#sel-${slugify(selection.name)}`,
            icon: rolled.inPlay ? 'live' : 'exposure',
            score: selectionMatch.score + liveBonus,
            badge: { label: money(selection.net, { round: true }), tone: toneOf(selection.net) },
          })
        }
      }
    }
  }

  /* ---- transfers, by reference or remark ---- */
  if (kinds.includes('transfer')) {
    for (const t of world.transfers) {
      if (!scope.has(t.fromId) && !scope.has(t.toId)) continue
      const match = bestFieldMatch('transfer', needle, [
        { text: t.ref, weight: SEARCH_SCORE.weight.primary, titleOffset: 0 },
        { text: t.remark, weight: SEARCH_SCORE.weight.tertiary },
      ])
      if (!match) continue

      const otherId = counterpartyIdOf(t, rootId)
      const other = world.byId.get(otherId)
      const recent = world.now - Date.parse(t.at) < 7 * 86_400_000
      const verb = t.fromId === rootId ? 'Gave' : t.toId === rootId ? 'Took' : 'Downline'

      byKind.transfer.push({
        id: `transfer:${t.id}`,
        kind: 'transfer',
        title: t.ref,
        subtitle: `${lll(t.at)} · ${verb} ${other?.username ?? EMPTY}`,
        ...(t.remark ? { meta: t.remark } : {}),
        ranges: match.ranges,
        to: `/member/${otherId}?tab=statement&ref=${encodeURIComponent(t.ref)}`,
        icon: 'transfers',
        score: match.score + (recent ? SEARCH_SCORE.live.recentTransfer : 0),
        // Neutral, always. A give is not a loss and a take is not a win — the
        // Transfers page already fought this battle and the palette must not
        // reopen it with a green or red badge on a credit movement.
        badge: { label: money(t.amount, { round: true }), tone: 'muted' },
      })
    }
  }

  const groups: SearchGroup[] = kinds.map((kind) => {
    const sorted = [...byKind[kind]].sort(compareHits)
    return {
      kind,
      label: SEARCH_GROUP_LABEL[kind],
      hits: sorted.slice(0, limitPerKind),
      // BEFORE the cap, so the palette can say "showing 5 of 41" honestly.
      total: sorted.length,
    }
  })

  // Group order follows the best hit in each group, so typing a username floats
  // Agents to the top and typing a market name floats the Live book. A fixed
  // order would put the answer second on half the queries.
  groups.sort((a, b) => {
    const scoreA = a.hits[0]?.score ?? -1
    const scoreB = b.hits[0]?.score ?? -1
    if (scoreA !== scoreB) return scoreB - scoreA
    return SEARCH_DATA_KINDS.indexOf(a.kind) - SEARCH_DATA_KINDS.indexOf(b.kind)
  })

  return delay(
    { text: query.text, groups, exposureUnavailable: projection.stale },
    LATENCY.fast,
  )
}
